import { Platform } from "react-native";
import { create } from "zustand";

import {
  chatApi,
  cuesApi,
  ApiError,
  groupApi,
  presenceApi,
  SessionExpiredError,
  TimeoutError,
  venuesApi,
  type VenueCandidate,
} from "@/api";
import { getCurrentCoords, type Coords } from "@/lib/location";
import { recordPublishedAnchor } from "@/lib/presence-anchor-io";
import {
  createLaunchRefresh,
  type RefreshOptions,
} from "@/state/launch-refresh";
import {
  applyChatMessage,
  applyGroupMessage,
  createCoalescedRefresh,
} from "@/state/inbox-sync";
import {
  isAppInForeground,
  reconcileCueNotifications,
} from "@/lib/notifications";
import { stopStayVisible } from "@/lib/stay-visible";
import {
  HEARTBEAT_CACHED_FIX_MAX_AGE_MS,
  heartbeatVenuePatch,
  makePresencePing,
  startPresenceHeartbeat,
  stopPresenceHeartbeat,
} from "@/state/presence-heartbeat";
import {
  type RefreshScheduleOptions,
  runRefreshPhases,
} from "@/state/refresh-schedule";
import {
  registerStayVisibleHideHandler,
  registerStayVisibleVisibilityReader,
  useStayVisibleStore,
} from "@/state/stay-visible-store";
import { storage } from "@/lib/storage";
import { clampNearbyRadiusM } from "@/lib/nearby-radius";
import {
  nextRecoveryStep,
  type NearbyFailure,
} from "@/lib/nearby-recovery";
import { clearChatMessageCache } from "@/lib/chat-message-cache";
import { probeSpan } from "@/lib/timing-probe";
import { clearGroupCache } from "@/lib/group-cache";
import { createSessionSingleFlight } from "@/lib/single-flight";
import { captureException } from "@/lib/telemetry";
import { clearOutbox } from "@/state/outbox-store";
import { clearDashboardCache } from "@/lib/dashboard-cache";
import { registerSessionReset, SPACE_STATE_KEY } from "@/state/session-reset";
import { runHideWithRetry } from "@/state/visibility-hide-retry";
import {
  clearSentRowHolds,
  holdSentRow,
  releaseSentRow,
  SENT_ROW_LINGER_MS,
  stillLingering,
} from "@/state/sent-row-linger";
import {
  applyChatReveal,
  type ChatRevealUpdate,
} from "@/state/chat-reveal-sync";
import {
  mergeChatSession,
  needsChatSession,
} from "@/state/chat-session-merge";
import {
  mergeNearbyPage,
} from "@/state/nearby-page";
import { useAuthStore } from "@/state/auth-store";
import type {
  ChatSession,
  Cue,
  EndConversationOutcome,
  NearbyUser,
  PeopleFilterPreferences,
  SmallGroupInbox,
  Venue,
} from "@/types";
import {
  afterRefusal,
  refusalReasonFrom,
  screenStateForRefusal,
  shouldPublish,
  type RefusalReason,
} from "@/lib/visibility-intent";

export type LocationState = "idle" | "denied" | "device_location_off" | "unavailable";

export type NearbyError =
  | "permission_denied"
  | "device_location_off"
  | "location_timeout"
  /**
   * The position we hold is real but too old or too vague to publish.
   *
   * Distinct from `location_timeout` (we could not get a fix) and from
   * `request_failed` (we could not reach the server), because the action that
   * helps is different in each case and only this one is the user's device
   * catching up. It used to be reported as `request_failed`: the server refuses
   * `POST /presence/join` with a 400, the client saw a non-5xx it did not
   * recognise, and told the user "opncue is having trouble connecting" with a
   * Try again that re-ran the same stale fix.
   */
  | "location_stale"
  /**
   * The position is current but too vague for the range the user has chosen.
   *
   * Separate from `location_stale` because the actions differ and neither helps
   * with the other: waiting for a fresher reading does nothing about a fix that
   * is accurate to a kilometre, and the server says as much — "try moving
   * outside, or widening your range".
   */
  | "location_imprecise"
  | "offline"
  | "backend_unavailable"
  | "request_failed";
export type VisibilityStatus = "visible" | "invisible";

type PersistedSpaceState = {
  joined?: boolean;
  visibilityStatus?: VisibilityStatus;
  cuesManuallyPaused?: boolean;
};

/**
 * `refreshNearby` only: is this read one the user is waiting on?
 *
 * Everything else about a refresh is the same either way — same endpoint, same
 * gate, same first page. What differs is who owns the FAILURE. A read the user
 * asked for may take the screen to say it failed; a read a socket event asked
 * for may not, because the user is looking at a list that still has rows in it
 * and did not ask for anything.
 *
 * Deliberately not part of `RefreshOptions`: the launch gate has no opinion on
 * this and would carry a field it never reads.
 */
export type NearbyRefreshOptions = RefreshOptions & {
  /** Event-driven convergence. Failures stay silent while rows are on screen. */
  background?: boolean;
};

type SessionState = {
  venue: Venue | null;
  nearby: NearbyUser[];
  /**
   * Where the next page of nearby people starts; null once there is no more.
   *
   * Only ever handed straight back to the server. The pages are keyed on a
   * value that cannot change while someone reads — see `NearbyResult` — so a
   * person never moves between pages under the scroll.
   */
  nearbyCursor: string | null;
  /**
   * Whether the server PROVED somebody beyond the loaded pages. Drives the
   * "N+" marker; `nearbyCursor` drives paging. They are different questions —
   * a budget-exhausted scan hands back a cursor having proved nothing.
   */
  nearbyHasMore: boolean;
  /**
   * Whether the list on screen is more than the first page.
   *
   * Read by Home to decide whether a stale list may be re-read on focus.
   * `refreshNearby` is the first page BY DEFINITION — it drops the cursor and
   * every page that was scrolled in — so re-reading a list somebody has paged
   * through rewinds it and loses their place. That is a worse answer than a
   * row a couple of minutes old, and it is the regression this flag keeps out.
   */
  nearbyPaged: boolean;
  /**
   * Is the people list actually on screen right now?
   *
   * Written by Home from its own `peopleListVisible` — the same flag that
   * decides whether person rows are pushed at all — and read by the socket
   * layer, which has no other way to know. It lives in the store rather than
   * in a ref because the reader is `_layout`, mounted ABOVE Home: there is no
   * prop path between them, and a module-level ref would survive a sign-out.
   *
   * The one consumer is the coarse `nearby:changed` nudge. That event names
   * nobody, so it has nothing to correct except a list somebody is looking at
   * — and it reaches every socket in the covering CELLS (8-12 of them at a
   * 10 km radius in a product latitude, ~1,000-1,500 km2 against a 314 km2
   * eligibility circle; 66 in the worst case near the pole clamp). Reading on
   * every one of those, from a phone showing Chat, is the read amplification
   * Stage 4 traded the publish cost for.
   */
  nearbyListVisible: boolean;
  /**
   * A coarse nudge arrived while nothing was showing.
   *
   * Not a queue and not a count — one bit, because the event carries nothing
   * to accumulate. It exists because the focus read is age-gated at two
   * minutes (ten on a paged list), so a nudge that arrives a second after a
   * read would otherwise be swallowed for the rest of that window and the
   * list would be knowingly stale when it came back on screen.
   */
  nearbyDirty: boolean;
  /**
   * Whether anyone is nearby with the filters off (the server sends 1/0 since
   * the step-10 count removal; this side only ever asks `> 0`) — meaningful
   * when the rendered list is empty and a filter is set, null otherwise.
   *
   * What separates "nobody's around" from "nobody matches your filters". The
   * client used to answer that from `nearby.length`, which worked only while
   * filtering happened here: once the server filters, an empty list is an empty
   * list either way and the screen would tell someone in a crowded room that
   * nobody is there, with the one action that fixes it hidden.
   */
  nearbyUnfilteredTotal: number | null;
  /**
   * In-flight guard for `loadMoreNearby`, so scroll momentum cannot fire the
   * same page twice. Separate from `loadingNearby`, which is the whole list
   * reloading: one paints the screen, the other paints a footer.
   */
  loadingMoreNearby: boolean;
  inbox: Cue[];
  sentCues: Cue[];
  /**
   * Every conversation currently loaded, both sections flattened.
   *
   * Kept flat because several screens look a conversation up by id and do not
   * care which tab it belongs to. What is *not* derivable from it any more is
   * how many conversations exist — `chatSectionTotals` carries that, because
   * this array is now a page rather than the whole inbox.
   */
  chats: ChatSession[];

  /** True section sizes, independent of how much has loaded. */
  chatSectionTotals: { live: number; inactive: number };

  /** Null once a section has nothing further to load. */
  chatCursors: { live: string | null; inactive: string | null };

  /** In-flight guard, so scroll momentum cannot fire the same page twice. */
  loadingMoreChats: boolean;

  /**
   * The Open tab's rows, kept SEPARATELY from `chats` rather than derived
   * from it.
   *
   * Every Active row already carries `peerOpenUntil`, so filtering `chats`
   * client-side looks like the obvious way to build this list. It is wrong for
   * exactly one reason, and it is the same reason the server refused to filter
   * a fetched page: `chats` is a PAGE. A contact who is open but sits below the
   * page boundary would simply not appear, and the tab would quietly under-
   * report — worst for the people with the most conversations, who need it most.
   *
   * So Open is asked for as its own section (`?section=open`), which applies
   * the predicate in SQL and pages properly. `peerOpenUntil` on Active rows is
   * still what draws the arc there; it is not a substitute for this list.
   */
  openChats: ChatSession[];
  /**
   * Whether the LAST attempt to read Open failed.
   *
   * Separate from `openSettled`, which says only that an attempt finished —
   * and which is set in a `finally`, so it is true after a read that threw.
   * The two were conflated, and the empty state read `openSettled` plus a null
   * cursor as "the whole section has been seen". A first load that failed
   * satisfies both (the cursor is still at its initial null), so the tab
   * announced "Nobody is open right now" having never successfully read
   * anything — a feature silently reporting itself dead, which is exactly the
   * fallback AGENTS.md forbids.
   *
   * `openSettled` stays as it is. Its own doc explains why it settles rather
   * than succeeds: a success-only flag parks a tab on a spinner forever after
   * one failed launch fetch. This flag adds the distinction that decision left
   * out, rather than reversing it.
   */
  openFailed: boolean;
  /** The Open section's real size, not `openChats.length`. */
  openTotal: number;
  /** Null once Open has nothing further to load. */
  openCursor: string | null;
  loadingMoreOpen: boolean;
  /**
   * Whether each tab fetch has SETTLED — resolved or rejected — at least once
   * this session.
   *
   * The tab screens need to tell "this list is empty" apart from "this list
   * has not been asked for", and they used to answer with a local flag their
   * own focus effect set. That flag knew nothing about `refreshAll`, which
   * fires every one of these fetches at session load — so a first visit to a
   * tab sat on a full-screen spinner re-asking a question the store had
   * already answered during Home's own load (measured at ~1s on device, with
   * the answer present the whole time).
   *
   * Settled, not succeeded, and each write sits in a `finally`: the screens'
   * previous local flags flipped after `Promise.allSettled` regardless of
   * outcome, and a success-only flag would park a tab on its spinner forever
   * after one failed launch fetch. What settling on a failure shows is the
   * same empty state the screens showed before — no worse, just not hidden
   * behind a redundant re-fetch.
   *
   * Cleared by `resetSpaceState`: "answered" was true of the PREVIOUS
   * account's session, and the next account's first paint must wait for its
   * own data rather than trust a flag describing someone else's.
   */
  inboxSettled: boolean;
  sentCuesSettled: boolean;
  chatsSettled: boolean;
  openSettled: boolean;
  groupInboxSettled: boolean;
  /**
   * ONE array for both sections, merged by id, exactly as `chats` is — the
   * inbox screen splits rows by `state`. Sectioned paging lives beside it:
   * active groups always arrive whole (a finite plan limit bounds them), and
   * `groupInactiveCursor`/`groupInactiveTotal` carry the inactive section's
   * next page and true size.
   */
  groupInbox: SmallGroupInbox[];
  groupInactiveCursor: string | null;
  groupInactiveTotal: number;
  loadingMoreGroups: boolean;
  sentCueIds: string[];
  unreadByChat: Record<string, number>;
  activeChatId: string | null;
  activeGroupId: string | null;
  loadingVenue: boolean;
  loadingNearby: boolean;
  /**
   * The space the viewer has opted into, mirrored from the server.
   *
   * This is the ONLY thing that decides which list is read. There is no
   * separate "picked mode" beside it, and there was: a `nearbyMode` field
   * held the same fact a second time, and the two could disagree — a relaunch
   * reset the mode and not the participation, so the filter sheet showed
   * Nearby selected under a hint reading "Same Space is on for X". Reported
   * from a device, and unfixable by keeping them in step, because there is no
   * moment at which they are BOTH known: the participation arrives from the
   * server one round trip after the client has already had to render
   * something.
   *
   * So the mode is derived (`effectiveNearbyMode`) and the two cannot part.
   * Participation and Nearby are exclusive by product rule, not by
   * coincidence: choosing Nearby LEAVES, which is why there is no state where
   * somebody is joined to a space and reading the radius list.
   *
   * The SERVER holds the real participation; this is the mirror the UI reads
   * so the two do not each decide independently, and the radius list's first
   * page carries it back on every launch.
   */
  sameSpaceVenueId: string | null;
  /**
   * The NAME of the joined space, carried beside its id.
   *
   * Kept here rather than read off `venue`, which is the inferred current
   * venue and is rewritten by the heartbeat — so labelling the mode from it
   * made the screen name whichever business the resolver last picked, not the
   * room the user actually joined.
   */
  sameSpaceVenueName: string | null;
  /**
   * Whether that participation is currently ANSWERING — the person is still
   * inside the space they joined.
   *
   * The header drops "Near" only when this is true: a joined space is the
   * user's own claim, but only while they are in it. The name is kept either
   * way, because the opt-in still stands and they need to be able to see and
   * clear it.
   */
  sameSpaceActive: boolean;
  locationState: LocationState;
  nearbyError: NearbyError | null;
  /**
   * A failure we are quietly retrying on the user's behalf.
   *
   * Separate from `nearbyError` because a failure being RECORDED and a failure
   * being SHOWN stopped being the same event: while this is true the screen
   * shows the ordinary spinner and `nearbyError` is deliberately null, so the
   * user is never handed a Try again button for something we are already trying
   * again ourselves. See `lib/nearby-recovery.ts` for which failures qualify
   * and for the bound on how long this may stay true.
   */
  nearbyRecovering: boolean;
  /**
   * The user has read a failure they alone can fix, and asked to get on with it.
   *
   * Only ever set for a failure retrying cannot change. It hides the
   * full-screen block; it does NOT pretend the failure is over, so the people
   * panel stays withheld and nothing claims the room is empty when the truth is
   * that we cannot see into it.
   */
  nearbyErrorAcknowledged: boolean;
  /**
   * The server's standing refusal to publish this person's CURRENT fix, or
   * null. Never a substitute for `visibilityStatus`, which is the user's own
   * switch — "Live" is the conjunction of the two.
   */
  publishBlocked: RefusalReason | null;
  spaceHydrated: boolean;
  joined: boolean;
  visibilityStatus: VisibilityStatus;
  cuesManuallyPaused: boolean;

  hydrateSpaceState: () => Promise<void>;
  resetSpaceState: () => Promise<void>;
  /**
   * Re-read the current space. Skipped when an identical read is in flight or
   * completed moments ago — pass `{ force: true }` for anything the user asked
   * for directly, or after a change the gate cannot observe (a new location
   * grant). See `state/launch-refresh.ts`.
   */
  /**
   * Resolves the space and, when appropriate, publishes presence.
   *
   * Returns whether it actually READ. `false` means the launch gate coalesced
   * this call into one already in flight or just finished — the caller did no
   * work and learned nothing, so it must not record this as a completed read.
   */
  refreshVenue: (options?: RefreshOptions) => Promise<boolean>;
  /** Re-read who is nearby, from the first page. Same gating as `refreshVenue`. */
  /**
   * Re-reads people from the first page. Returns whether it actually READ, for
   * the same reason as `refreshVenue`: a coalesced call is not a read.
   */
  refreshNearby: (options?: NearbyRefreshOptions) => Promise<boolean>;
  /**
   * Put a standing, user-fixable failure behind you without pretending it is
   * fixed. Deliberately not a retry — see `NearbyErrorState`.
   */
  acknowledgeNearbyError: () => void;
  /**
   * Append the next page of nearby people.
   *
   * A no-op with nothing left to load or a page already in flight, so a list
   * that fires this on every scroll frame costs one request.
   */
  loadMoreNearby: () => Promise<void>;
  /**
   * THE way to change the discovery radius, from any surface — the Home
   * filter sheets and the profile editor both go through here so neither can
   * hold a stale copy that silently writes an old value back. Persists to the
   * profile, then forces the nearby re-read (the freshness gate cannot see a
   * radius change). Returns whether anything was written, so callers with
   * radius-scoped state of their own (Home's groups list) know to re-read it.
   */
  saveNearbyRadius: (radiusM: number) => Promise<boolean>;
  /**
   * THE two ways the list changes scope. There is no third — no "show the
   * space I am already in", because the mode is derived from participation
   * and is therefore already showing it.
   *
   * Both drop the cursor. A cursor is issued against a scope and the server
   * refuses one from the other, so carrying it over would only buy a wasted
   * round-trip to be told to start again.
   */
  enterSameSpace: () => Promise<void>;
  leaveSameSpace: () => Promise<void>;
  /**
   * THE way to change the nearby filters, for the same reason
   * `saveNearbyRadius` is THE way to change the radius.
   *
   * They are a saved preference now, not screen state: the server applies them
   * to the list AND to the nearby alert, which fires with the app closed. A
   * filter the server cannot see is a filter it cannot honour, and the alert
   * would wake someone about a person they had explicitly excluded.
   *
   * Persists, then forces the nearby re-read — the freshness gate cannot see a
   * filter change, so skipping it makes changing a filter look broken. Returns
   * whether anything was written.
   */
  savePeopleFilters: (
    filters: PeopleFilterPreferences | null,
  ) => Promise<boolean>;
  refreshInbox: () => Promise<void>;
  refreshSentCues: () => Promise<void>;
  refreshChats: () => Promise<void>;
  /**
   * Load the Open section.
   *
   * Deliberately NOT part of `refreshAll`. Launch already costs five requests
   * and the Open tab is not on the path to first paint; adding a sixth would
   * undo the arrival work for a list most sessions never open. It loads when
   * the tab is focused, which is also when its contents are most likely to
   * still be true — the whole section is built from leases measured in hours.
   */
  refreshOpenChats: () => Promise<void>;
  loadMoreOpenChats: () => Promise<void>;
  /**
   * Make sure ONE conversation is loaded, by id.
   *
   * The inbox pages, so a screen opened from a notification, a deep link, or
   * from below the first page cannot assume the list holds its conversation.
   * Without this the chat screen rendered from `undefined`: "Someone", a
   * stranger's avatar, no share-more state and an empty Report target.
   */
  ensureChat: (chatId: string) => Promise<void>;
  loadMoreChats: (section: "live" | "inactive") => Promise<void>;
  loadMoreInactiveGroups: () => Promise<void>;
  refreshGroupInbox: () => Promise<void>;
  refreshAll: (options?: RefreshScheduleOptions) => Promise<void>;
  searchVenues: (q: string, coords?: Coords) => Promise<VenueCandidate[]>;
  nearbyVenues: (coords?: Coords) => Promise<VenueCandidate[]>;
  joinVenue: (candidate: VenueCandidate, coords?: Coords) => Promise<void>;
  joinSpace: () => Promise<void>;
  setVisibility: (visible: boolean) => Promise<void>;
  setCueAvailabilityPaused: (paused: boolean) => Promise<void>;
  receiveCue: (cue: Cue) => void;
  removeNearbyUser: (userId: string) => void;
  /**
   * Note that the people list is (or is no longer) on screen. Idempotent: the
   * setter short-circuits an unchanged value, because Home calls it from an
   * effect that re-runs whenever the panel re-renders.
   */
  setNearbyListVisible: (visible: boolean) => void;
  /**
   * Record that something near the user changed while nobody was looking.
   *
   * Deliberately does NOT read. The whole point is that an unwatched screen
   * costs nothing; the flag is drained by Home's reconcile the moment the list
   * is visible again, and cleared by any completed read.
   */
  markNearbyDirty: () => void;
  /**
   * `patch` is the socket payload's own `preview` and `sentAt`. With it the
   * inbox row is updated in place; without it — a cue acceptance, a reveal —
   * the inbox is read instead.
   */
  receiveChatMessage: (
    chatId: string,
    messageId?: string,
    patch?: { preview?: string; sentAt?: string },
  ) => void;
  receiveChatReveal: (
    chatId: string,
    update: ChatRevealUpdate,
  ) => boolean;
  receiveGroupMessage: (
    groupId: string,
    patch?: { preview?: string; sentAt?: string },
  ) => void;
  setActiveChat: (chatId: string | null) => void;
  markChatRead: (chatId: string) => void;
  setActiveGroup: (groupId: string | null) => void;
  markGroupRead: (groupId: string) => void;
  endChat: (chatId: string, outcome?: EndConversationOutcome) => Promise<void>;
  /** Wipe my own copy of a thread. Not an ending — the conversation stays. */
  clearChat: (chatId: string) => Promise<ChatSession>;
  /**
   * Lift a block from the conversation it was placed on.
   *
   * Not a reopen. It returns the pair to each other's nearby list; the thread
   * resumes only if one of them sends a Cue and the other accepts.
   */
  unblock: (chatId: string) => Promise<ChatSession>;
  /** Ask to reopen an Inactive conversation. Carries no message, by design. */
  requestReopen: (chatId: string) => Promise<ChatSession>;
  /**
   * Walk back into a conversation you quietly left.
   *
   * Separate from `requestReopen` because that one notifies the other person
   * and this one must never be able to: they were never told anything happened.
   */
  resume: (chatId: string) => Promise<ChatSession>;
  respondToReopen: (chatId: string, accept: boolean) => Promise<ChatSession>;
  sendCue: (toUserId: string) => Promise<void>;
  respondToCue: (
    cueId: string,
    status: "accepted" | "ignored" | "blocked",
  ) => Promise<ChatSession | null>;
  /**
   * Withdraw a cue you sent that has not been answered. Frees the connection
   * slot immediately; the recipient stays counted against the 24-hour outreach
   * allowance, so this is not a way to reset the day.
   */
  cancelSentCue: (cueId: string) => Promise<void>;
};

async function persistSpaceState(state: PersistedSpaceState) {
  await storage.set(SPACE_STATE_KEY, JSON.stringify(state));
}

function unique(value: string, values: string[]): string[] {
  return values.includes(value) ? values : [...values, value];
}

/**
 * People with a Cue in flight, in either direction — the ones Nearby hides
 * because there is nothing left to do about them from that screen.
 *
 * `sentCues` is filtered by status here and not taken wholesale, because it
 * stopped being a pending queue: it is now a ledger that also carries recently
 * accepted Cues. Reading it wholesale would quietly extend this rule to
 * "everyone you have connected with in the last day", removing people from
 * Nearby who belong there — a product change smuggled in as a list change.
 */
function pendingCuePeerIds(inbox: Cue[], sentCues: Cue[]): Set<string> {
  return new Set([
    ...inbox.map((cue) => cue.fromUserId),
    ...sentCues
      .filter((cue) => cue.status === "pending")
      .map((cue) => cue.toUserId),
  ]);
}

/** Same reasoning: the "Cue sent" mark on a Nearby card means one is waiting. */
function pendingSentPeerIds(sentCues: Cue[]): string[] {
  return sentCues
    .filter((cue) => cue.status === "pending")
    .map((cue) => cue.toUserId);
}

function withoutPendingCuePeers(
  nearby: NearbyUser[],
  inbox: Cue[],
  sentCues: Cue[],
): NearbyUser[] {
  const hiddenIds = pendingCuePeerIds(inbox, sentCues);
  const now = Date.now();
  // The grace lives here rather than at the call site because this is the ONE
  // filter every path shares — `refreshSentCues` is what removes the row today,
  // but `refreshInbox` applies the same rule and would reintroduce the flash.
  return nearby.filter(
    (user) => !hiddenIds.has(user.id) || stillLingering(user.id, now),
  );
}

/**
 * Auto-continue an empty first paint while a cursor remains (roadmap step 10,
 * client rule). Rendered-empty is not server-empty: `withoutPendingCuePeers`
 * runs after the page arrives, so a page can filter to nothing on this side
 * while `nearbyCursor` says more people exist — and a screen that says
 * "nobody around" with pages left unread is a false empty. Harmless against
 * today's full-or-final pages; load-bearing once the eligibility flip lets
 * the server return short pages on budget exhaustion.
 *
 * Bounded at three consecutive pages, and the bound is a GIVE-UP, not proof
 * of exhaustion: after three pages that all rendered to nothing, the screen
 * shows peopleEmpty even though people may remain behind the cursor. That
 * state is accepted deliberately (roadmap step 10) — the alternative is an
 * unbounded chain of fetches on a busy street where every candidate has a
 * Cue in flight, and the next scroll or refresh continues from the surviving
 * cursor anyway.
 *
 * One loop at a time. A refresh that starts mid-loop simply leaves the
 * running loop to continue against the fresh state it re-reads every
 * iteration — `loadMoreNearby`'s own in-flight and cursor-drift guards keep
 * a stale page from ever landing.
 *
 * TRIGGERED BY SUBSCRIPTION, not by call sites (second review round: the
 * first build fired only after `refreshNearby`, and the same empty state is
 * created by every later refilter of the list in place — `refreshInbox`,
 * `refreshSentCues`, the sent-row linger expiry, plus two the review missed,
 * `receiveCue` and `removeNearbyUser`). Five call sites is a sixth-site bug
 * waiting to happen; the subscription below watches the STATE, so whoever
 * empties the list — including code not written yet — gets the same
 * continuation.
 *
 * `nearbyGiveUpCursor` is what makes a subscription safe: after the cap the
 * emptied-with-cursor state PERSISTS, and a trigger that keyed on the state
 * alone would relaunch the chase on the very next store write. The marker
 * remembers the cursor the fill gave up on, and its scope is ONE refresh
 * epoch: a completed refresh clears it (fourth review round — keyed on the
 * cursor value alone it outlived its evidence, because three swallowed page
 * FAILURES leave the cursor unmoved, making the marker equal the next
 * identical refresh's own cursor and silently denying the recovered network
 * its sanctioned retry). Within an epoch it suppresses every retrigger —
 * inbox writes, linger expiries — which is correct even as cues resolve:
 * the filter only ever removes rows, so a mid-epoch rechase could gain
 * nothing but the deeper pages the give-up already declined. A state where
 * the list has people — or has genuinely ended — clears it too.
 */
export const NEARBY_PAGE_CHASE_MAX_REQUESTS = 3;
let nearbyAutoFillInFlight = false;
let nearbyGiveUpCursor: string | null = null;
let nearbyChaseGiveUpCursor: string | null = null;

async function autoFillNearbyIfRenderedEmpty(
  get: () => Pick<SessionState, "nearby" | "nearbyCursor" | "loadMoreNearby">,
): Promise<void> {
  if (nearbyAutoFillInFlight) return;
  nearbyAutoFillInFlight = true;
  try {
    if (get().nearby.length > 0 || get().nearbyCursor === null) return;
    // ONE call, not a loop (eighth review round). `loadMoreNearby` now owns the
    // whole request budget — empty pages AND failed ones — so looping here
    // MULTIPLIED it: three iterations x three requests was nine fetches on the
    // open path, against a comment and a test that both said three. The budget
    // is one number spent in one place, which is the only way the claimed
    // bound and the real one stay equal.
    await get().loadMoreNearby();
    // Budget spent with the screen still empty: the accepted give-up state.
    // Remember which continuation was given up on, while the in-flight flag
    // still holds the subscription off.
    if (get().nearby.length === 0) nearbyGiveUpCursor = get().nearbyCursor;
  } finally {
    nearbyAutoFillInFlight = false;
  }
}

/**
 * Whether two saved filter sets say the same thing.
 *
 * An all-null set and no set at all are deliberately equal — both narrow
 * nothing, so treating them as different would write a profile update and
 * re-read the whole list every time someone opened the filter sheet and closed
 * it again without choosing anything.
 */
function samePeopleFilters(
  a: PeopleFilterPreferences | null,
  b: PeopleFilterPreferences | null,
): boolean {
  const norm = (f: PeopleFilterPreferences | null) => ({
    ageBandMinFloor: f?.ageBandMinFloor ?? null,
    ageBandMaxFloor: f?.ageBandMaxFloor ?? null,
    language: f?.language ?? null,
    friendshipGoal: f?.friendshipGoal ?? null,
  });
  const left = norm(a);
  const right = norm(b);
  return (
    left.ageBandMinFloor === right.ageBandMinFloor &&
    left.ageBandMaxFloor === right.ageBandMaxFloor &&
    left.language === right.language &&
    left.friendshipGoal === right.friendshipGoal
  );
}

function nearbyErrorFromLocationStatus(
  status: Exclude<LocationState, "idle">,
): NearbyError {
  switch (status) {
    case "denied":
      return "permission_denied";
    case "device_location_off":
      return "device_location_off";
    case "unavailable":
      return "location_timeout";
  }
}

function isOfflineError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes("network is unreachable") ||
    message.includes("internet connection appears to be offline") ||
    message.includes("no internet") ||
    message.includes("unable to resolve host") ||
    message.includes("unknownhostexception")
  );
}

function isBackendConnectionError(error: unknown): boolean {
  if (error instanceof TimeoutError) return true;
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes("connectexception") ||
    message.includes("failed to connect") ||
    message.includes("connection refused") ||
    message.includes("econnrefused") ||
    message.includes("server unreachable")
  );
}

/**
 * The server's refusal to publish a position, as distinct from a failure to
 * reach it.
 *
 * `VisibilityNeedsLocationException` is a 400 carrying
 * `code: "visibility_needs_location"`. Matched on the code rather than the
 * status: a bare 400 from this endpoint could be several things, and treating
 * every one of them as a location problem would be the same mistake in the
 * other direction.
 */
export function visibilityRefusalReason(error: unknown): RefusalReason | null {
  if (!(error instanceof ApiError) || error.status !== 400) return null;
  return refusalReasonFrom(error.body);
}

/**
 * The refusal reason, as a screen state.
 *
 * `integrity_hold` deliberately shares the stale copy: the server keeps that
 * one vague on purpose — naming the rule that caught it tells whoever is
 * probing it what to change — and the hold lapses on its own within minutes,
 * so "we need a fresh reading" is both true and the right thing to wait for.
 */
/**
 * The state every successful publish implies, and the only place that is said.
 *
 * `publishBlocked` describes the server's refusal of the CURRENT fix, so any
 * write it accepts makes that refusal untrue. Clearing it only in `refreshVenue`
 * was a real seam: `joinVenue`, `joinSpace`, `setVisibility(true)` and unpausing
 * cues all publish too, and after any of them the client went on believing
 * publishing was blocked while the server had already published the user. That
 * is the same lie as the original bug pointing the other way — someone
 * discoverable, told they are not.
 *
 * Going hidden clears it for the same reason: a refusal about publishing says
 * nothing once there is nothing to publish, and leaving it set would show the
 * next visible state as blocked until some later refresh happened to clear it.
 *
 * `published-state.test.ts` scans this file and fails if `presenceApi.join()`
 * is called anywhere but `publishPresence`, so a sixth call site cannot quietly
 * reintroduce the seam on either side.
 */
const PUBLISH_SETTLED = { joined: true, publishBlocked: null } as const;

/**
 * Publish presence, and record what the server said about the attempt.
 *
 * The only place `presenceApi.join()` is called. `PUBLISH_SETTLED` covered the
 * SUCCESS side at every call site, and review found the mirror still open: a
 * refused publish outside `refreshVenue` set nothing at all. So a user who was
 * locally visible could correct their venue, be refused, see a generic "update
 * failed" toast from `edit-location-editor.tsx` — and keep a Live pill, while
 * the server had declined to publish them. The same lie the whole of this work
 * has been removing, reached by a fourth route.
 *
 * Rethrows, always. Whether a refusal deserves a screen state is the caller's
 * question — `refreshVenue` owns the screen and answers yes; a venue correction
 * shows its own toast — but whether the client believes the user is discoverable
 * is not a per-caller decision, so it is settled here.
 *
 * **Does not stop the heartbeat.** The obvious move, and wrong: each beat calls
 * `checkIn` and then `join`, so a running heartbeat is precisely what refreshes
 * a stale position and retries the publish. Stopping it on refusal would remove
 * the automatic recovery and leave the user dark until they acted — which is
 * the failure this work exists to remove, reintroduced as a tidy-up.
 */
/**
 * Whether an explicit go-visible tap's lease renewal has not yet reached the
 * server.
 *
 * Set by the three tap sites BEFORE they hand control to anything that might
 * publish, cleared the moment `publishPresenceExplicit` succeeds, and cleared
 * when the user goes invisible (a superseded intent owes nothing). While it is
 * owed, EVERY publish — including the heartbeat's — goes through the renewing
 * endpoint, because the heartbeat's retry loop is deliberately how a refused
 * toggle converges (measured on Android: jumpy fixes refuse the tap's own
 * publish routinely, and the next beat is what lands it). Without this, the
 * race is: `onBecameVisible` starts the heartbeat, its immediate beat
 * publishes through non-renewing `join()`, the explicit request fails — and
 * the user is visible but unleased.
 *
 * A phone left on a table never sets this: only a human tap does, and one
 * successful renewal clears it. The abandoned-device invariant holds.
 */
let explicitRenewalOwed = false;

async function publishPresence(): Promise<void> {
  // The tap's renewal outranks the plain join while it is owed — whichever
  // request lands first renews, and the race above disappears.
  if (explicitRenewalOwed) return publishPresenceExplicit();
  try {
    await presenceApi.join();
    useSessionStore.setState({ ...PUBLISH_SETTLED });
    correctStalePublish();
  } catch (error) {
    const refusal = visibilityRefusalReason(error);
    if (refusal) useSessionStore.setState({ publishBlocked: refusal });
    throw error;
  }
}

/**
 * A publish that landed into a superseded intent re-asserts the hide.
 *
 * The race (review, 2026-08-26): a go-visible request already on the wire
 * cannot be recalled by the seq guard — that guard sits BEFORE the publish and
 * stops stale follow-up work, not a request in flight. If the user taps
 * Hidden while it is airborne and the hide completes first, the late visible
 * lands second and the server ends `visible = true` for somebody who chose
 * hidden; the hide loop has already settled and re-arms nothing.
 *
 * So the publish's own completion is the correction point: this runs after the
 * server has answered the publish, which is an ORDERING GUARANTEE the client
 * otherwise never gets — a hide sent from here is processed after that
 * publish, definitively. `hideOnServer` is safe to re-drive: its loop checks
 * `isCurrent` each round and the attempt is idempotent, so re-telling the
 * server costs one request and cannot fight a loop already running.
 *
 * The symmetric race — a late HIDE landing after a newer go-visible tap — is
 * deliberately not handled here: while the intent is visible the heartbeat is
 * running, and its next beat republishes within one interval. Hidden has no
 * such reconciler, which is exactly why it needs this one. And behind both
 * sits the same backstop the hide-retry module documents: with the heartbeat
 * stopped, nothing refreshes `last_seen`, so even a missed correction lapses
 * within `presenceTtl`.
 */
function correctStalePublish(): void {
  if (useSessionStore.getState().visibilityStatus === "visible") return;
  void hideOnServer(visibilityIntentSeq);
}

/**
 * The same publish, through the endpoint that also renews the visibility
 * lease — for EXPLICIT go-visible taps only.
 *
 * `POST /presence/visibility {visible:true}` is gate-for-gate identical to
 * `POST /presence/join` (assertActive, assertPublishable, arrival
 * classification, the same refusal shapes) plus the lease renewal. The server
 * cannot renew in `join()` because the background stay-visible task calls it;
 * a human tapping a switch is the signal it cannot fake, so the tap's own
 * request carries it (owner decision, 2026-08-26 — the trailing Nearby refresh
 * was the renewal path before this, and a renewal that depends on a different
 * request succeeding is not deterministic).
 *
 * NOT used by `publishPresence`'s other callers on purpose: the foreground
 * heartbeat's `join` IS `publishPresence`, so folding this in there would let
 * a phone that is merely open — or forgotten face-up on a table — renew its
 * own lease on a 120-second timer forever, which is the exact abandoned-device
 * load the lease exists to expire. The two automatic republish paths stay on
 * `join()` for the same reason; app-open renewal already happens through
 * `GET /presence/available` when Home focuses.
 */
async function publishPresenceExplicit(): Promise<void> {
  try {
    await presenceApi.setVisibility(true);
    explicitRenewalOwed = false;
    useSessionStore.setState({ ...PUBLISH_SETTLED });
    correctStalePublish();
  } catch (error) {
    const refusal = visibilityRefusalReason(error);
    if (refusal) useSessionStore.setState({ publishBlocked: refusal });
    throw error;
  }
}

/**
 * The user's latest visibility intent, as a monotonic token.
 *
 * Every switch flip claims the next number. Anything doing slow network work on
 * behalf of an older flip checks this before it acts, and stands down when it
 * has been superseded — which is what stops a hide that has been retrying for
 * twenty seconds from landing AFTER the user has turned themselves back on and
 * quietly making them invisible again.
 */
let visibilityIntentSeq = 0;

/** The intent behind a hide that has not reached the server yet, if any. */
let unsettledHide: number | null = null;

/**
 * Tell the server this person is hidden, and keep trying until it lands.
 *
 * The loop is `runHideWithRetry`, which imports nothing so a real test can
 * drive it — this store pulls `react-native`, `@/api` and `@/i18n` and cannot
 * be loaded by one. Here is only the wiring: our call, our intent token, and
 * what to remember when the attempts run out.
 */
async function hideOnServer(seq: number): Promise<void> {
  const outcome = await runHideWithRetry({
    attempt: async () => {
      await presenceApi.setVisibility(false);
    },
    isCurrent: () => seq === visibilityIntentSeq,
    sleep: (ms) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
  });
  if (outcome === "settled" && unsettledHide === seq) unsettledHide = null;
  if (outcome === "unsettled") unsettledHide = seq;
}

/**
 * Re-attempt a hide that never reached the server, on returning to the app.
 *
 * The retry list above is deliberately short — a phone in a tunnel is not
 * helped by a sixth attempt eight seconds after the fifth. Coming back to the
 * foreground is the moment connectivity has plausibly changed, so that is when
 * it is worth asking again. A no-op when there is nothing outstanding.
 */
export function retryUnsettledHide(): void {
  const seq = unsettledHide;
  if (seq === null || seq !== visibilityIntentSeq) return;
  void hideOnServer(seq);
}

/**
 * Move the switch NOW, and return the token this intent owns.
 *
 * The whole point: `set` here re-renders the switch on the tap that caused it,
 * so nothing the server does afterwards decides how responsive the control
 * feels. Every caller below used to publish first and set second, which made
 * the switch's latency the LOCATION STACK's latency — sub-second on iOS and
 * web, but up to twelve seconds on Android, where `getLastKnownPositionAsync`
 * often returns nothing (no recent requester, or an approximate-only grant that
 * fails the accuracy floor) and there is no fallback to cut the wait short.
 *
 * Refusals still reach the user. They arrive as `publishBlocked`, which
 * `isLive` folds into what the switch DISPLAYS, so a publish the server turned
 * down shows Hidden with a reason while `visibilityStatus` keeps holding the
 * user's actual intent. That split is pre-existing and deliberate.
 */
function claimVisibilityIntent(patch: {
  visibilityStatus: VisibilityStatus;
  cuesManuallyPaused?: boolean;
  joined?: boolean;
}): number {
  const seq = ++visibilityIntentSeq;
  useSessionStore.setState({
    joined: patch.joined ?? true,
    publishBlocked: null,
    visibilityStatus: patch.visibilityStatus,
    ...(patch.cuesManuallyPaused === undefined
      ? {}
      : { cuesManuallyPaused: patch.cuesManuallyPaused }),
  });
  return seq;
}

/** Adapts the pure state machine to this store's field names. */
function afterRefusalState(
  desired: VisibilityStatus,
  reason: RefusalReason,
): { publishBlocked: RefusalReason } {
  return { publishBlocked: afterRefusal({ desired }, reason).blocked };
}

export function nearbyErrorForRefusal(reason: RefusalReason): NearbyError {
  return screenStateForRefusal(reason);
}

function nearbyErrorFromRequest(error: unknown): NearbyError {
  const refusal = visibilityRefusalReason(error);
  if (refusal) return nearbyErrorForRefusal(refusal);
  if (isOfflineError(error)) return "offline";
  if (error instanceof ApiError && error.status >= 500) {
    return "backend_unavailable";
  }
  if (isBackendConnectionError(error)) return "backend_unavailable";
  return "request_failed";
}

/**
 * The retry we run instead of the user.
 *
 * Module-level rather than store state because none of it is anything a screen
 * should render — it is bookkeeping for a timer. What the screen reads is
 * `nearbyRecovering`, which these keep in step.
 *
 * `recoveryRunning` exists because `recoveryTimer` is null for the whole time
 * an attempt is actually in flight, and without it a venue leg that happened to
 * succeed mid-recovery would reset the budget below and the retries could go on
 * forever. That is the exact shape of unboundedness this file is not allowed to
 * have.
 */
let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
let recoveryAttempts = 0;
let recoveryRunning = false;

/** A retry is scheduled or in flight — the recovery owns the screen until it resolves. */
function recoveryInProgress(): boolean {
  return recoveryTimer !== null || recoveryRunning;
}

function cancelScheduledRecovery(): void {
  if (recoveryTimer !== null) {
    clearTimeout(recoveryTimer);
    recoveryTimer = null;
  }
}

/**
 * Drop a recovery entirely — no retry, no spinner, no spent attempts.
 *
 * For sign-out and space reset, where the session the retry belonged to no
 * longer exists. Leaving a timer armed across a sign-out would refresh presence
 * for an account that is on its way out.
 */
export function cancelNearbyRecovery(): void {
  cancelScheduledRecovery();
  recoveryAttempts = 0;
  recoveryRunning = false;
}

/**
 * A path that succeeded, expressed as a state patch.
 *
 * Returns nothing at all while a recovery is in progress, which is the point: a
 * venue read that succeeds while the nearby list is still failing must not clear
 * the spinner, cancel the pending retry, or hand back the attempts already
 * spent. Only the recovery itself decides it is finished.
 */
function clearedNearbyFailure(): {
  nearbyError?: null;
  nearbyRecovering?: false;
  nearbyErrorAcknowledged?: false;
} {
  if (recoveryInProgress()) return {};
  recoveryAttempts = 0;
  return {
    nearbyError: null,
    nearbyRecovering: false,
    nearbyErrorAcknowledged: false,
  };
}

/**
 * Record a failed refresh, and decide whether the user ever hears about it.
 *
 * The single funnel. Every path that used to `set({ nearbyError })` goes
 * through here, because "which failures are ours to fix" is one rule and five
 * copies of it is how the screen ends up offering a Try again button for a
 * condition no tap can change.
 *
 * The bound is `nextRecoveryStep`, which stops at `MAX_AUTO_ATTEMPTS`. Spending
 * the budget surfaces the failure AND resets the count, so a later, unrelated
 * failure gets its own attempts — the reset needs a fresh trigger and cannot be
 * reached from inside a running recovery.
 */
function recordNearbyFailure(failure: NearbyError, error?: unknown): void {
  // The server's own answer to "when should you come back", when it gave one.
  // `client.ts` attaches it to `ApiError` for any 429/503; read structurally so
  // this funnel does not have to import the error class to ask.
  const asked = (error as { retryAfterMs?: unknown } | null | undefined)
    ?.retryAfterMs;
  const step = nextRecoveryStep(failure as NearbyFailure, recoveryAttempts, {
    retryAfterMs: typeof asked === "number" ? asked : undefined,
  });
  cancelScheduledRecovery();

  if (step.action === "surface") {
    recoveryAttempts = 0;
    useSessionStore.setState({
      nearbyError: failure,
      nearbyRecovering: false,
      // A failure the user has never seen cannot already be dismissed. Without
      // this, one acknowledgement would silence every later failure too.
      nearbyErrorAcknowledged:
        useSessionStore.getState().nearbyError === failure &&
        useSessionStore.getState().nearbyErrorAcknowledged,
    });
    return;
  }

  recoveryAttempts = step.attempt;
  // The failure is deliberately NOT written to `nearbyError`: the screen keeps
  // its spinner, and nothing that reads that field can paint an error for a
  // request we are about to make again.
  useSessionStore.setState({ nearbyError: null, nearbyRecovering: true });
  recoveryTimer = setTimeout(() => {
    recoveryTimer = null;
    void runNearbyRecovery();
  }, step.delayMs);
}

/** Did the attempt just made record a fresh failure — scheduled or surfaced? */
function recoveryFailedAgain(): boolean {
  return (
    recoveryTimer !== null || useSessionStore.getState().nearbyError !== null
  );
}

/**
 * One retry attempt: exactly what the Try again button did, pressed for them.
 *
 * Both legs, in the same order the screen needs them — the space first, then
 * the people in it — because the button re-read the space and the list followed
 * from it. Retrying only the failed leg would leave a user whose venue read
 * failed with a stale place under a fresh list.
 *
 * The second leg is skipped once the first has already recorded a failure, so
 * one cycle can never consume two attempts.
 */
async function runNearbyRecovery(): Promise<void> {
  recoveryRunning = true;
  try {
    await useSessionStore.getState().refreshVenue({ force: true });
    if (!recoveryFailedAgain()) {
      await useSessionStore.getState().refreshNearby({ force: true });
    }
  } catch {
    // `refreshNearby` rethrows and `refreshVenue` does not, but both have
    // already routed the reason through `recordNearbyFailure` on the way out.
    // There is nothing left here to decide, and rethrowing from a timer
    // callback would be an unhandled rejection.
  } finally {
    recoveryRunning = false;
  }

  if (!recoveryFailedAgain()) {
    recoveryAttempts = 0;
    useSessionStore.setState({
      nearbyError: null,
      nearbyRecovering: false,
      nearbyErrorAcknowledged: false,
    });
  }
}

/**
 * Keep presence fresh for as long as the app is open and the user is visible.
 *
 * Its own function because two quite different paths need it and only one of
 * them is a state *change*. `onBecameVisible` covers becoming visible;
 * `refreshVenue` covers the far more common case of an already-visible user
 * re-focusing the home tab, which publishes directly and never
 * goes near `onBecameVisible`. Missing that second path would leave the
 * heartbeat off for exactly the people who never toggle anything.
 */
/**
 * Record where the user is, and put the resolved space on screen.
 *
 * One definition, used by the heartbeat and by every path that is about to ask
 * the server to publish this person. Two copies of these two lines is how the
 * beat and the button end up disagreeing about what a check-in does.
 */
async function checkInAt(coords: Coords): Promise<Venue | null> {
  // BOTH captured before the request goes out, for two different reasons.
  // The owner: the client bakes the bearer token into headers at request
  // start, so the userId read in the same synchronous moment is the identity
  // the server will attribute this check-in to — reading it AFTER the await
  // stamped account B onto a response that belonged to account A's token
  // whenever a sign-out/sign-in landed mid-flight, and a wrongly-stamped
  // anchor is one B's read-side refusal cannot refuse. The epoch: the same
  // rule every post-await store write follows (`sessionEpoch`) — a reset
  // between request and response means this result vouches for a session
  // that is over, so the anchor write is discarded rather than re-littering
  // the key sign-out just cleared.
  const ownerId = useAuthStore.getState().session?.userId;
  const epoch = sessionEpoch;
  const venue = await venuesApi.current(coords);
  applyHeartbeatVenue(venue);
  // The device's anchor cache mirrors every successful publication (roadmap
  // step 11): the fix the server just accepted IS the anchor the background
  // movement gate compares against. Foreground publishes reset it too, or the
  // first background wake after backgrounding would measure movement against
  // wherever the phone was when the task last published. Best-effort inside —
  // a failed cache write degrades to "publish next wake", never breaks a
  // check-in. No session at request time, no stamp, no write.
  if (ownerId && epoch === sessionEpoch) {
    await recordPublishedAnchor({
      ownerId,
      lat: coords.lat,
      lng: coords.lng,
      accuracyM: coords.accuracyMeters,
    });
  }
  return venue;
}

/**
 * Write a current position before asking the server to publish it.
 *
 * `POST /presence/join` sets `last_seen`, `visible` and `status` and touches no
 * coordinates at all — only `checkIn`, through `GET /venues/current`, records a
 * position. So joining on its own refreshed LIVENESS while pinning POSITION,
 * and the server would republish a fix from hours or days ago as current. It now
 * refuses to, which turns a silent inaccuracy into a visible failure unless the
 * client does this first.
 *
 * Deliberately best-effort. If no fix is available this returns without joining
 * being blocked, and the server's refusal is what the user sees — one place
 * deciding whether a position is good enough, and it is not this one. Throwing
 * here would mean two different rules disagreeing about the same fix.
 *
 * The same two calls in the same order as `presence-heartbeat.ts` and
 * `stay-visible.ts`, for the reason documented there.
 */
async function freshenPositionBeforeJoining(): Promise<void> {
  try {
    // A BOUNDED read, not a bare one. This used to insist on a live fix on the
    // reasoning that a button somebody pressed deserves one — but measured on
    // build 12, becoming visible cost 8.95 seconds of spinner, and this call
    // plus `refreshVenue`'s identical one were the two live reads inside it.
    //
    // The bound is the heartbeat's own, and that is the argument: the beat
    // publishes a fix up to `HEARTBEAT_CACHED_FIX_MAX_AGE_MS` old unattended,
    // and the stored coordinate is allowed to reach twice that between beats.
    // Demanding better from a tap than we accept from the timer was never a
    // rule, just an asymmetry — and unlike a Small Group's pin, presence
    // self-corrects on the very next beat.
    const result = await getCurrentCoords({
      acceptCachedWithinMs: HEARTBEAT_CACHED_FIX_MAX_AGE_MS,
    });
    if (result.status !== "ok") return;
    await checkInAt(result.coords);
  } catch {
    // A failed check-in is not this function's problem to report: the join
    // below either succeeds on the position already stored, or is refused with
    // a message that says which part was wrong.
  }
}

/**
 * Put the space the heartbeat just resolved on the screen.
 *
 * The decision is `heartbeatVenuePatch` — pure, and in `presence-heartbeat.ts`
 * so it can be driven by a real test rather than asserted against its own
 * source. This is only the store write.
 *
 * `locationState` and `nearbyError` are deliberately untouched. They describe
 * what happened on a read the user asked for, and a background beat has no
 * business clearing an error message the user has not had a chance to read.
 */
function applyHeartbeatVenue(venue: Venue | null) {
  const state = useSessionStore.getState();
  const patch = heartbeatVenuePatch(state.venue?.id ?? null, venue);
  if (patch) useSessionStore.setState(patch);
}

function beginPresenceHeartbeat(options?: { beatNow?: boolean }) {
  void startPresenceHeartbeat({
    // Position first, then visibility — see `makePresencePing`. `join()` alone
    // would keep `last_seen` fresh while never updating the coordinates behind
    // it, which is how a heartbeat turns "they went invisible while sitting
    // still" into the worse "they stayed cueable at a place they have left".
    ping: makePresencePing({
      getFix: async () => {
        // The one caller that accepts a cached fix. Every other path here takes
        // the live read, because they are all things a person asked for. Safe
        // only because the fix's age travels with it — see `fix-age.ts`.
        const result = await getCurrentCoords({
          acceptCachedWithinMs: HEARTBEAT_CACHED_FIX_MAX_AGE_MS,
        });
        return result.status === "ok" ? result.coords : null;
      },
      // The current intent, read at the moment of asking — never captured.
      // `visibilityStatus` is the intent half of the switch (it stays
      // "visible" through a refusal), and it flips to "invisible"
      // SYNCHRONOUSLY at the hide tap — before `hideOnServer` is awaited —
      // which is exactly what lets an airborne beat see the change in time.
      stillWantsVisible: () =>
        useSessionStore.getState().visibilityStatus === "visible",
      // Writes the resolved space into the store as well as to the server.
      //
      // It used to discard it, on the reasoning that the heartbeat owns the
      // server's view and `refreshVenue` owns the screen's. That split is what
      // a walk test found: the server followed the user correctly — this call
      // is `GET /venues/current`, which re-resolves the space from the fix and
      // checks in — while Home kept displaying the space they had been in when
      // they first connected, for as long as they never re-focused the tab.
      // Someone walking with the app open watched a label that had stopped
      // being true, which is worse than a label that updates a beat late.
      checkIn: checkInAt,
      // The heartbeat's own publish, and the one that runs while nobody is
      // looking. `beatOnce` swallows a failed beat on purpose — a bad
      // connection is not something the user can act on — but a REFUSAL is not
      // a failed request: the server is telling us this person is no longer
      // publishable, and swallowing that left the pill reading Live for
      // somebody who had silently stopped being discoverable, for as long as
      // the tab stayed open.
      //
      // Recorded here rather than inside `presence-heartbeat.ts`, which takes
      // its dependencies as arguments and imports nothing — this seam belongs
      // to the store, not to the timer.
      //
      // Deliberately does NOT set `nearbyError`. Replacing a working screen out
      // of a background beat is its own defect; what this owes the user is an
      // honest visibility indicator, which `publishBlocked` gives.
      join: publishPresence,
    }),
    isForeground: isAppInForeground,
  }, options);
}

/**
 * Called when the user becomes visible. If they've already enabled background
 * ("stay visible while locked"), start the keep-alive; otherwise ask (respecting
 * the mode + backoff rules in the stay-visible store).
 */
function onBecameVisible(venueId: string | null) {
  // FIRST, and above the web guard below. Presence expires on a server clock
  // that nothing in the foreground app was restarting, so an open app went
  // stale on the table while still showing `Visible`. That is not a native
  // concern — it is worst on web, which has no background task at all.
  beginPresenceHeartbeat();

  // Background visibility is a native concept; skip on web.
  if (Platform.OS === "web") return;
  const sv = useStayVisibleStore.getState();
  if (sv.mode === "enabled") {
    // Verify health + (re)start the background task; repairs on break.
    void sv.verify();
  } else {
    sv.maybePrompt(venueId);
  }
}

function onBecameInvisible() {
  // A superseded intent owes nothing: going hidden cancels any renewal a
  // failed go-visible tap left outstanding, or a later beat would renew a
  // lease for a visibility the user has switched off.
  explicitRenewalOwed = false;
  // Also first, and also unguarded: whatever platform started a heartbeat has
  // to be able to stop one. Beating on after the user has gone invisible would
  // hold their presence row fresh for a visibility they have switched off.
  stopPresenceHeartbeat();

  if (Platform.OS === "web") return;
  void stopStayVisible();
}


// De-dupe realtime message deliveries. The `/events` socket can deliver the
// same `chat:message` more than once (e.g. several live connections during dev
// hot reload / reconnects), which would otherwise inflate the unread badge.
// We count each message id only once. Bounded so it can't grow unbounded.
const SEEN_MESSAGE_LIMIT = 500;
const seenMessageIds = new Set<string>();

/**
 * Shared by every caller of `refreshVenue` / `refreshNearby`, which is the
 * point: Home reaches the store directly rather than through the tab layout's
 * resync gate, so gating one caller would collapse nothing.
 */
const launchRefresh = createLaunchRefresh();

/**
 * Which sign-in the store's contents belong to. Bumped by `resetSpaceState`.
 *
 * Sign-out clears the lists, but it cannot recall a request already on the
 * wire: the client bakes the bearer token into headers before fetch, so the
 * old account's inbox can resolve AFTER the reset — and an unconditional
 * `set()` behind an await would write the previous account's rows back into a
 * store the next account's screens now trust on first paint. Every list write
 * that follows an await captures this number first and discards its result if
 * a reset happened in between; the settled flags obey the same rule, because
 * a stale promise must not vouch for data it did not write.
 *
 * Deliberately NOT applied to the venue/nearby paths: they were left
 * byte-identical by owner decision (2026-08-21), their screens do not trust a
 * settled flag, and `loadMoreNearby` already drops cross-reset pages via its
 * cursor check. If they ever gain first-paint trust, they join this rule.
 */
let sessionEpoch = 0;

/**
 * The reconciling reads behind `receiveChatMessage` / `receiveGroupMessage`.
 *
 * Both inbox endpoints are expensive — each opens with a maintenance UPDATE,
 * and the group one loads every message of every group the user belongs to — so
 * neither may run once per received message. The row itself is patched from the
 * socket payload; these converge only what the payload cannot carry, and their
 * cost is bounded by the window rather than by how much anyone is talking.
 */
const chatsReconcile = createCoalescedRefresh(() =>
  useSessionStore.getState().refreshChats(),
);
const groupInboxReconcile = createCoalescedRefresh(() =>
  useSessionStore.getState().refreshGroupInbox(),
);

function markMessageSeen(messageId: string): boolean {
  if (seenMessageIds.has(messageId)) return false;
  seenMessageIds.add(messageId);
  if (seenMessageIds.size > SEEN_MESSAGE_LIMIT) {
    const oldest = seenMessageIds.values().next().value;
    if (oldest !== undefined) seenMessageIds.delete(oldest);
  }
  return true;
}

/**
 * Apply ONE page's unread counts to the badge map.
 *
 * Three read paths carry unread counts — the inbox's two sections, and Open —
 * and they must agree, because they all render the same badge from the same
 * map keyed by chat id. They did not: `loadMoreChats` set positive counts and
 * DELETED zero ones, while `refreshOpenChats` merged only the positives and
 * `loadMoreOpenChats` touched the map not at all. So a conversation read on
 * another device kept its badge on the Open tab until a full refresh, and
 * Open's second page carried no badges whatsoever. One function, so the rule
 * cannot differ per caller again.
 *
 * Only the rows this page carried are touched. The rest of the map is another
 * section's truth, and a page that overwrote it would clear badges for
 * conversations it knows nothing about.
 *
 * The chat currently open is excluded because it is being read right now — its
 * mark converges through `markRead`, and a badge over the screen you are
 * looking at is noise.
 */
function withPageUnread(
  current: Record<string, number>,
  page: readonly ChatSession[],
  activeChatId: string | null,
): Record<string, number> {
  const next = { ...current };
  for (const chat of page) {
    if ((chat.unreadCount ?? 0) > 0 && chat.id !== activeChatId) {
      next[chat.id] = chat.unreadCount as number;
    } else {
      delete next[chat.id];
    }
  }
  return next;
}

/**
 * The Open section read currently in flight, and the session it belongs to.
 *
 * Two independent things ask for this section, and on entering the tab they can
 * ask in the same instant: the focus effect, and the effect that re-reads when
 * a loaded lease has lapsed. That was two requests for one answer, and it also
 * made the "one request per expiry boundary" cost claim untrue.
 *
 * A shared PROMISE rather than a boolean guard. A boolean lets the second
 * caller return immediately, and both call sites do work after the await —
 * they resample the clock — so an early resolve would have them act on data
 * that had not arrived yet. Handing back the in-flight promise makes the second
 * caller wait for the answer the first is already fetching.
 *
 * Module-level rather than store state, deliberately: no screen renders it, and
 * putting it in the store would publish a new object to every subscriber twice
 * per request.
 *
 * ## Why the epoch is stored with it
 *
 * Module-level in-flight work outlives a session, and the first version of this
 * did not say so. A sign-out mid-request left the promise in the slot, the next
 * session's read joined it, and that promise then wrote nothing — its own epoch
 * guard had already made its results unwelcome. So the new session never issued
 * a read of its own and its Open tab sat on a spinner until a focus or a manual
 * refresh. No data crossed the boundary, but the tab was stranded.
 *
 * `resetSpaceState` already cancels the other module-level work for exactly
 * this reason (`chatsReconcile.cancel()`, `clearSentRowHolds()`), and adding a
 * fourth line there would have worked. Binding the epoch to the SLOT is
 * better: the rule then lives with the thing it governs and cannot be
 * forgotten by whoever writes the next reset path. A slot from a previous
 * session is simply never joined.
 *
 * The mechanics live in `createSessionSingleFlight` rather than inline, because
 * these few lines had two defects in two passes and neither was reachable by a
 * test that reads this file. There they are driven directly.
 */
const openRefresh = createSessionSingleFlight<void>();

/** Which people list this session is reading. */
export type NearbyMode = "radius" | "same_space";

/**
 * The ONE place that decides which endpoint a page comes from.
 *
 * Both the first-page refresh and the paging chase read through here, because
 * the alternative is the same `if` written twice — and the two copies would
 * only have to disagree once for a cursor issued against one list to be sent
 * to the other. The server would refuse it and paging would silently restart,
 * which is the kind of bug that looks like a flaky list.
 *
 * The two endpoints are wire-identical by design; the mode is the only
 * difference, so nothing downstream of this function needs to know about it.
 */
function readPeoplePage(mode: NearbyMode, cursor?: string) {
  return mode === "same_space"
    ? presenceApi.sameSpace(cursor)
    : presenceApi.available(cursor);
}

/**
 * Which list is being read. Derived, never stored.
 *
 * Same Space IS participation — the server only answers for somebody who has
 * joined a space — and choosing Nearby is what leaves. So "which list" is not
 * an independent fact and must not be an independent field: holding it
 * separately produced a screen that said Same Space was on while showing
 * Nearby as the selected option, because a relaunch reset the mode and left
 * the participation on the presence row.
 *
 * Participation SHOULD outlive a restart. Somebody who joined the bar and
 * closed the app is still in the bar, still visible, and still someone the
 * room should see. Deriving the mode from it is what makes the screen agree
 * with the server about that, on the first paint after the radius list's
 * first page brings the participation back.
 *
 * Takes the state object rather than the id so the call sites read as a
 * question about the session, and so adding a second input later cannot
 * silently miss one of them.
 */
export function effectiveNearbyMode(state: {
  sameSpaceVenueId: string | null;
}): NearbyMode {
  return state.sameSpaceVenueId ? "same_space" : "radius";
}

export const useSessionStore = create<SessionState>((set, get) => ({
  venue: null,
  nearby: [],
  nearbyCursor: null,
  nearbyHasMore: false,
  nearbyPaged: false,
  nearbyListVisible: false,
  nearbyDirty: false,
  nearbyUnfilteredTotal: null,
  loadingMoreNearby: false,
  inbox: [],
  sentCues: [],
  chats: [],
  chatSectionTotals: { live: 0, inactive: 0 },
  chatCursors: { live: null, inactive: null },
  openChats: [],
  openFailed: false,
  openTotal: 0,
  openCursor: null,
  loadingMoreOpen: false,
  loadingMoreChats: false,
  inboxSettled: false,
  sentCuesSettled: false,
  chatsSettled: false,
  openSettled: false,
  groupInboxSettled: false,
  groupInbox: [],
  groupInactiveCursor: null,
  groupInactiveTotal: 0,
  loadingMoreGroups: false,
  sentCueIds: [],
  unreadByChat: {},
  activeChatId: null,
  activeGroupId: null,
  loadingVenue: false,
  loadingNearby: false,
  sameSpaceVenueId: null,
  sameSpaceVenueName: null,
  sameSpaceActive: false,
  locationState: "idle",
  nearbyError: null,
  nearbyRecovering: false,
  nearbyErrorAcknowledged: false,
  publishBlocked: null,
  spaceHydrated: false,
  joined: false,
  visibilityStatus: "invisible",
  cuesManuallyPaused: false,

  async hydrateSpaceState() {
    if (get().spaceHydrated) return;

    try {
      const raw = await storage.get(SPACE_STATE_KEY);
      const saved = raw ? (JSON.parse(raw) as PersistedSpaceState) : {};
      set({
        joined: saved.joined ?? false,
        visibilityStatus: saved.visibilityStatus ?? "invisible",
        cuesManuallyPaused: saved.cuesManuallyPaused ?? false,
        spaceHydrated: true,
      });
    } catch {
      set({ spaceHydrated: true });
    }
  },

  async resetSpaceState() {
    // FIRST, synchronously: everything already awaiting a response captured
    // the old epoch, and this is the moment their results stop being welcome.
    sessionEpoch += 1;
    // A scheduled reconcile would otherwise fire against a session that is gone.
    chatsReconcile.cancel();
    groupInboxReconcile.cancel();
    // Same reasoning: a hold is a promise about a row on this session's screen.
    clearSentRowHolds();
    // A session reset is a superseded-intent boundary, the same invariant as
    // going invisible: whatever renewal a tap in the OLD session left
    // outstanding, the new one owes nothing — without this, the first
    // automatic republish after re-login would renew a lease no tap in this
    // session asked for.
    explicitRenewalOwed = false;
    // And a heartbeat outlives the sign-out that ends it otherwise: the timer
    // belongs to the module, not the store, so nothing else here would stop it
    // refreshing presence for an account that has gone.
    stopPresenceHeartbeat();
    // Same again for the nearby retry: its timer is module state too, and an
    // attempt that fired after sign-out would refresh presence for an account
    // that is already on its way to the welcome screen.
    cancelNearbyRecovery();
    // And the cached conversation pages are private message content. They live
    // only in memory, but the next account to sign in on this device shares the
    // process, so they go now rather than at the next garbage collection.
    clearChatMessageCache();
    clearGroupCache();
    // Unsent messages are this account's private content too, and an outbox
    // carried across a sign-out would deliver one person's message from
    // another person's session.
    clearOutbox();
    // And the dashboard snapshot, for the same reason: one account's goals,
    // reflections and activity totals, held in memory by a shared process.
    clearDashboardCache();
    set({
      spaceHydrated: true,
      joined: false,
      visibilityStatus: "invisible",
      cuesManuallyPaused: false,
      unreadByChat: {},
      activeChatId: null,
      groupInbox: [],
      groupInactiveCursor: null,
      groupInactiveTotal: 0,
      loadingMoreGroups: false,
      activeGroupId: null,
      locationState: "idle",
      nearbyError: null,
      nearbyRecovering: false,
      nearbyErrorAcknowledged: false,
      // For the same reason the message cache is dropped above: these are other
      // people's names, headlines and bios, held in memory by a process the next
      // account to sign in on this device shares. They were being left behind —
      // the error was cleared and the list it described was not.
      nearby: [],
      nearbyCursor: null,
      nearbyHasMore: false,
      nearbyPaged: false,
      nearbyUnfilteredTotal: null,
      loadingMoreNearby: false,
      // And the tab lists themselves, for the same reason yet again: received
      // Cues, sent Cues and the conversation list are the previous account's
      // private data, and they were surviving sign-out in memory. Latent while
      // the tabs re-fetched behind a local spinner before rendering anything;
      // a leak the moment any screen trusts the store on first paint.
      inbox: [],
      sentCues: [],
      sentCueIds: [],
      chats: [],
      chatSectionTotals: { live: 0, inactive: 0 },
      chatCursors: { live: null, inactive: null },
      openChats: [],
      openFailed: false,
      openTotal: 0,
      openCursor: null,
      loadingMoreOpen: false,
      loadingMoreChats: false,
      // "Answered" was true of the previous account's session. Left set, the
      // next account's tabs would paint the (now empty) lists as settled truth
      // instead of waiting for their own first fetch.
      inboxSettled: false,
      sentCuesSettled: false,
      chatsSettled: false,
      openSettled: false,
      groupInboxSettled: false,
    });
    await storage.remove(SPACE_STATE_KEY);
  },

  async refreshVenue(options) {
    // Cold launch reaches this from two places in the same frame — the tab
    // layout's `refreshAll` and Home's focus effect — and each acquisition is
    // budgeted at up to 12s. Whoever arrives second rides the first one's read.
    // Coalesced into another caller's read: report that nothing was read, so
    // no caller can mistake a skip for a completed one.
    if (!launchRefresh.begin("venue", options)) return false;
    set({ loadingVenue: true });
    try {
      await get().hydrateSpaceState();

      // Bounded, for the same reason as `freshenPositionBeforeJoining` — and
      // here it also removes a DUPLICATE. On the become-visible path that
      // function has just read a fix and called `checkInAt`, which resolves
      // the venue through this very endpoint and puts it on screen. This read
      // then re-acquired a position seconds old, because a bare read disables
      // the cached short-circuit by construction, and asked the same question
      // again. With the bound it is answered from the fix the first read left
      // behind, at no cost.
      const result = await getCurrentCoords({
        acceptCachedWithinMs: HEARTBEAT_CACHED_FIX_MAX_AGE_MS,
      });
      let venue: Venue | null;

      if (result.status === "ok") {
        try {
          venue = await venuesApi.current(result.coords);
          set({
            venue,
            locationState: "idle",
            ...clearedNearbyFailure(),
          });
        } catch (error) {
          set({ venue: null, locationState: "idle" });
          recordNearbyFailure(nearbyErrorFromRequest(error), error);
          throw error;
        }
      } else {
        // A refusal or a device-level switch is a standing condition, and the
        // screen has to say so. A failed read is not: it is almost always a
        // slow fix, and this runs on every focus of the home tab, so treating
        // one as the other is what replaced a correct space with an error and
        // then resolved it on the next pull-to-refresh.
        //
        // The venue already resolved stays the best answer we have until
        // something says otherwise, so it is kept rather than discarded.
        const keepVenue = result.status === "unavailable" ? get().venue : null;

        set({
          venue: keepVenue,
          locationState: keepVenue ? "idle" : result.status,
          ...(keepVenue ? clearedNearbyFailure() : {}),
        });
        if (!keepVenue) {
          recordNearbyFailure(nearbyErrorFromLocationStatus(result.status));
        }
        return true;
      }

      if (venue) {
        if (
          shouldPublish({
            desired: get().visibilityStatus,
            cuesManuallyPaused: get().cuesManuallyPaused,
            blocked: get().publishBlocked,
          })
        ) {
          await publishPresence();
          // Re-checked AFTER the await, against current state: the decision
          // above was made before a publish that can take seconds, and a hide
          // tap during it means everything below — the heartbeat, and above
          // all the stay-visible restart — would resurrect visibility the
          // user just switched off. The corrector inside `publishPresence`
          // has already re-asserted the hide server-side; this keeps the
          // client from undoing it.
          if (
            !shouldPublish({
              desired: get().visibilityStatus,
              cuesManuallyPaused: get().cuesManuallyPaused,
              blocked: get().publishBlocked,
            })
          ) {
            return true;
          }
          // The join above is one write; this keeps it fresh for as long as the
          // tab stays open. This branch runs on every focus of the home tab, so
          // it — not `onBecameVisible` — is how the heartbeat starts for a user
          // who was already visible when they reopened the app.
          //
          // `beatNow: false`: `publishPresence()` a few lines up already wrote
          // presence, so an immediate beat would repeat `checkIn` + `join`
          // against a row written milliseconds ago. Harmless for one user, two
          // wasted requests per client in a cold-open wave — and this is the
          // cold-open path.
          beginPresenceHeartbeat({ beatNow: false });
          // Resume the background keep-alive on relaunch for opted-in users —
          // verify health/(re)start the task, repairing on break.
          if (
            Platform.OS === "ios" &&
            useStayVisibleStore.getState().mode === "enabled"
          ) {
            void useStayVisibleStore.getState().verify();
          }
        } else {
          await presenceApi.setVisibility(false);
          set({ ...PUBLISH_SETTLED, visibilityStatus: "invisible" });
          // Symmetric with the branch above: this path can turn a visible
          // session invisible without going through `onBecameInvisible`, and a
          // heartbeat left running would keep refreshing presence for someone
          // the server has just been told to hide.
          stopPresenceHeartbeat();
        }
      }
    } catch (error) {
      const refusal = visibilityRefusalReason(error);
      if (refusal) {
        // The space resolved — `/venues/current` answered 200 immediately
        // before this — so blanking it would throw away a good answer over a
        // refusal about something else.
        //
        // `visibilityStatus` is the user's own switch and is NOT touched. The
        // first version of this set it to "invisible" so the header would stop
        // claiming Live, which was right about the header and wrong about
        // everything else: the branch above reads that same field to choose
        // between `join()` and `setVisibility(false)`, so the next refresh took
        // the user's intent as a decision to hide and published them as
        // invisible. Retrying with a perfect fix left them dark until they
        // toggled it by hand. `publishBlocked` carries the refusal instead, and
        // `isLive()` is the conjunction — see `lib/visibility-intent.ts`.
        // `publishPresence` has already recorded the block; this adds the
        // screen state, which is `refreshVenue`'s to own.
        //
        // The heartbeat is deliberately left RUNNING — see `publishPresence`.
        // An earlier version stopped it here, which removed the one thing that
        // recovers on its own: every beat re-checks in and retries the publish.
        set({ ...afterRefusalState(get().visibilityStatus, refusal) });
        recordNearbyFailure(nearbyErrorForRefusal(refusal));
      } else if (get().nearbyError === null && !recoveryInProgress()) {
        set({ venue: null });
        recordNearbyFailure(nearbyErrorFromRequest(error), error);
      }
    } finally {
      launchRefresh.end("venue");
      set({ loadingVenue: false });
    }
    // Reached only by a call that actually read — the coalesced skip returned
    // above, and every other exit states its own answer.
    return true;
  },

  async refreshNearby(options) {
    // Coalesced into another caller's read: see `refreshVenue`.
    if (!launchRefresh.begin("nearby", options)) return false;
    /** A background failure swallowed below — released, but not stamped fresh. */
    let suppressed = false;
    set({ loadingNearby: true });
    try {
      // Show visible people near the current GPS point, not only exact venue matches.
      // No cursor: a refresh is the first page by definition, and whatever had
      // been paged in is dropped with it. Merging a fresh first page into stale
      // later pages is the one thing keyset paging cannot survive — the later
      // pages were read against a population this one has already replaced.
      // `total` still arrives on the wire as a deprecated shim (the presented
      // page length) and is deliberately not read: the headcount is derived
      // from the rendered list and the cursor since the step-10 count removal.
      // The question this read asks, captured so the answer can be checked
      // against it below. Not re-derived afterwards: the mode can move under
      // the await — a join or a leave running concurrently — and comparing a
      // page to a mode it was never read with is how the wrong rows get
      // labelled.
      const asked = effectiveNearbyMode(get());
      let page = await readPeoplePage(asked);
      // The consent read, and it only ever arrives on the RADIUS list.
      //
      // It records WHAT the user is participating in — and since the mode is
      // DERIVED from that, recording it can change which list should have
      // been read. On a cold launch it always does: the client starts with no
      // participation, asks the radius endpoint, and is told it is in a space.
      //
      // Writing this page after that is the bug an earlier version shipped:
      // the rows came from the radius query, and labelling them Same Space
      // presented strangers as people who had joined the same room. The fix
      // is not to leave the mode behind — that was the previous fix, and it
      // is what put "Same Space is on" above a selected Nearby button — but
      // to throw the page away and ask the right endpoint.
      //
      // Bounded to ONE extra read, structurally rather than by a counter: the
      // second read is the same-space endpoint, which does not carry this
      // field at all, so the branch cannot be taken twice.
      //
      // `undefined` means the server was not asked (a cursored page, or an
      // older build) and must change nothing. Only an explicit value speaks.
      const consent = page.sameSpace;
      if (consent !== undefined) {
        set({
          sameSpaceVenueId: consent?.venueId ?? null,
          sameSpaceVenueName: consent?.venueName ?? null,
          sameSpaceActive: consent?.active ?? false,
        });
      }
      if (effectiveNearbyMode(get()) !== asked) {
        page = await readPeoplePage(effectiveNearbyMode(get()));
      }
      const { people, totalUnfiltered, nextCursor, hasMore } = page;
      const visible = withoutPendingCuePeers(people, get().inbox, get().sentCues);
      // A completed refresh clears the give-up marker: the marker's scope is
      // ONE refresh epoch (fourth review round). Keyed on the cursor alone it
      // outlived its evidence — three SWALLOWED page failures leave the
      // cursor unmoved, so the marker equalled this refresh's own first-page
      // cursor and the roadmap's "self-corrects on the next refresh" was
      // false: the recovered network never got its retry. Cleared before the
      // set, so the write below and the finally's own both see it down. The
      // scroll chase's own marker clears here for the same reason: a refresh
      // re-reads the list from the top, so whatever a chase gave up on is
      // evidence about a population this read has just replaced.
      nearbyGiveUpCursor = null;
      nearbyChaseGiveUpCursor = null;
      set({
        nearby: visible,
        nearbyCursor: nextCursor,
        nearbyHasMore: hasMore ?? false,
        // First page by definition: whatever had been paged in is gone.
        nearbyPaged: false,
        // Whatever a coarse nudge was pointing at, this read has just seen.
        // Cleared HERE and nowhere else: only a completed read may claim the
        // list is caught up, so a failure — including the background failure
        // swallowed below — leaves the bit set for the next chance.
        nearbyDirty: false,
        nearbyUnfilteredTotal: totalUnfiltered ?? null,
        ...(get().locationState === "idle" && get().venue
          ? clearedNearbyFailure()
          : {}),
      });
    } catch (error) {
      // A dead session is not a failure to report. The user is already being
      // signed out and sent to the welcome screen, so painting "couldn't load
      // people" behind that, or rethrowing into the screen effects that call
      // this without a catch, only produces an error nobody can act on — which
      // is what put a crash overlay in front of the welcome screen.
      if (error instanceof SessionExpiredError) return false;
      // A convergence read nobody asked for must not seize the screen.
      //
      // `recordNearbyFailure` is the right funnel for a read the USER is
      // waiting on: it retries, and while it retries `nearbyRecovering` is
      // true. Home reads that as `locatingSpace`, and `locatingSpace` replaces
      // the whole FlatList with the "finding your space" spinner — so a single
      // failed background read wipes a populated list off the screen. Under
      // load that is not hypothetical: the arrivals that cause the reads are
      // the same arrivals that make them fail.
      //
      // Only with rows already on screen. An empty list has nothing to protect
      // and every reason to report, so the first read still routes normally —
      // this suppresses the takeover, never the diagnosis.
      //
      // Swallowed rather than rethrown for the same reason: the caller is a
      // socket event with no screen to report to. The next read the user
      // actually asks for — focus, pull-to-refresh, resume — records the
      // failure properly if it is still there.
      //
      // And it must actually be ABLE to. `suppressed` is what stops this
      // silence from also claiming freshness: `end` stamps `finishedAt`
      // unconditionally, which is harmless for every other failure — those
      // either surface or are retried by `runNearbyRecovery`, and the retry
      // forces past the gate — but this path does neither. Without it a failed
      // convergence would tell the gate the list had just been read, and the
      // next focus would skip for the whole max-age window: two minutes, or
      // ten on a paged list, whose longer bound was granted precisely BECAUSE
      // events were assumed to be converging it.
      if (options?.background && get().nearby.length > 0) {
        suppressed = true;
        return false;
      }
      recordNearbyFailure(nearbyErrorFromRequest(error), error);
      throw error;
    } finally {
      launchRefresh.end("nearby", { read: !suppressed });
      // This write is also what hands an empty-but-cursored refresh to the
      // auto-fill: the subscription below the store skips while
      // `loadingNearby` is true, so the fill starts from the state this
      // leaves behind, not from the page write above.
      set({ loadingNearby: false });
    }
    // Reached only by a read that completed: the coalesced skip returned above,
    // and every failure either rethrows or answers `false`.
    return true;
  },

  acknowledgeNearbyError() {
    set({ nearbyErrorAcknowledged: true });
  },

  async loadMoreNearby() {
    const cursor = get().nearbyCursor;
    // Nothing left, or a page already on its way. `onEndReached` fires on
    // scroll momentum and again on every re-layout, so without this one flick
    // at the bottom of the list becomes a burst of identical requests.
    if (!cursor || get().loadingMoreNearby || get().loadingNearby) return;
    // The chase that already gave up from exactly here (eighth review round).
    // `onEndReached` is re-armed by any CONTENT-LENGTH change, not only by new
    // rows: VirtualizedList suppresses a repeat only while the measured length
    // equals the one it last fired at (`_sentEndForContentLength`), and
    // `_onContentSizeChange` re-checks the edge. The paging footer changes that
    // length TWICE per chase — mounting when `loadingMoreNearby` goes true,
    // unmounting when it goes false — so a chase that ended empty would be
    // restarted by its own footer, and the client would walk the entire
    // remaining candidate list in bursts, at server cost, with the user doing
    // nothing. The marker holds until something real changes: people arrive,
    // the list genuinely ends, or a refresh re-reads it.
    if (cursor === nearbyChaseGiveUpCursor) return;

    set({ loadingMoreNearby: true });
    try {
      // ONE bounded chase, and the only page budget on either path — the
      // rendered-empty auto-fill calls this once rather than wrapping it in a
      // loop of its own. A page can come back EMPTY with a cursor still on it,
      // and one scroll must not dead-end on that. Two ways it happens: this
      // client filtered every row out (a whole page of people with Cues in
      // flight), or the SERVER's scan ran out of budget before it found
      // anybody live — which, since the lease became the only bound on
      // candidates, is a shape the read path now reports honestly instead of
      // calling the room empty.
      //
      // Without this the list simply does not grow, and the user sits at the
      // bottom of a list with more people behind it. The auto-fill cannot
      // cover it either: that one watches for an EMPTY list, and this list has
      // people on it.
      for (
        let attempt = 0;
        attempt < NEARBY_PAGE_CHASE_MAX_REQUESTS;
        attempt += 1
      ) {
        const from = get().nearbyCursor;
        if (!from) return;
        try {
          const { people, nextCursor, hasMore } = await readPeoplePage(
            effectiveNearbyMode(get()),
            from,
          );

          // The list may have been reloaded from the top while this was in
          // flight — pull-to-refresh, a radius change, a cancelled cue. That
          // page was read against a population this one no longer describes,
          // and appending it would leave a block of rows from the old read
          // stranded below the new first page. Drop it; the cursor it carried
          // is meaningless now too. Re-read per attempt, not once: a later
          // attempt is a later request and can be overtaken just the same.
          if (get().nearbyCursor !== from) return;

          const visible = withoutPendingCuePeers(people, get().inbox, get().sentCues);
          const merged = mergeNearbyPage(get().nearby, visible);

          set({
            nearby: merged,
            nearbyCursor: nextCursor,
            // The newest page's proof replaces the previous one: whether anybody
            // is beyond the list is a fact about where the list now ENDS.
            nearbyHasMore: hasMore ?? false,
            // Set even when the page came back empty. What matters to Home is
            // that the list is no longer the one a plain refresh would return:
            // a chase that spent requests to find that out has still moved the
            // cursor past where a re-read would put it back.
            nearbyPaged: true,
          });

          // Grew the list, or reached the true end of it. Either is an answer,
          // and either retires the marker.
          if (visible.length > 0 || nextCursor === null) {
            nearbyChaseGiveUpCursor = null;
            return;
          }
        } catch {
          // Swallowed on purpose, unlike `refreshNearby`. The list already on
          // screen is intact and correct; a failed page means the user has not
          // reached the rest yet, not that what they are looking at is wrong.
          // Replacing a working screen with "couldn't load people" because the
          // scroll ran past the end would be the worse answer — including for
          // an expired session, which is already signing the user out on its
          // own. It costs an ATTEMPT rather than ending the chase, which is
          // how the auto-fill's old outer loop gave a flaky network its retry;
          // the cursor is untouched, so the retry asks for the same page.
        }
      }
      // Budget spent with nothing to show — whether the pages came back EMPTY
      // or the requests FAILED. Record where, so the footer's own relayout
      // cannot immediately start the next chase.
      //
      // Failures are marked too (ninth review round). Exempting them left the
      // loop open on the worst input there is: a failed attempt does not move
      // the cursor, so an unmarked failed chase respins at the SAME cursor on
      // the next footer unmount, and a bad network moment becomes endless
      // three-request bursts with the user doing nothing. That is strictly
      // worse than the empty case this guard was written for, which at least
      // advanced through the list and terminated.
      //
      // The fourth round's concern — a marker that outlives its evidence and
      // denies a recovered network its retry — is answered by the EPOCH, not
      // by skipping the mark: any completed refresh clears this, as does a
      // page that finds people or reaches the true end. Recovery is a refresh,
      // never an automatic respin nobody asked for.
      nearbyChaseGiveUpCursor = get().nearbyCursor;
    } finally {
      set({ loadingMoreNearby: false });
    }
  },

  /**
   * Enter Same Space at the venue the user has just JOINED.
   *
   * Called only from the join flow, never from a bare toggle, and that is the
   * product rule rather than an implementation detail: choosing Same Space
   * prompts Join Space every time, because the server's guess at which room
   * somebody is in is wrong often enough that only the user can name it. The
   * join proved proximity; this records that the named place is the one they
   * want to be seen in.
   *
   * The POST goes FIRST and its failure is allowed to surface. A mode set
   * before the participation landed would show an empty list with no
   * explanation — the server would be answering for somebody who never opted
   * in — so nothing local changes until the server has agreed.
   */
  async enterSameSpace() {
    const joined = get().venue;
    if (!joined?.id) throw new Error("Join a space before entering Same Space");
    await presenceApi.joinSameSpace(joined.id);
    // The cursor goes with the scope: one issued against the radius list is
    // refused here, so dropping it turns a wasted round trip into a clean
    // first page.
    // The name is snapshotted WITH the id: `venue` moves under the heartbeat,
    // and the label has to keep naming the place that was actually joined.
    set({
      sameSpaceVenueId: joined.id,
      sameSpaceVenueName: joined.name,
      // Active by construction: the server just proved proximity to accept it.
      sameSpaceActive: true,
      nearby: [],
      nearbyCursor: null,
    });
    await get().refreshNearby({ force: true });
  },

  /**
   * Leave Same Space and go back to the radius list.
   *
   * The DELETE is NOT best-effort, and an earlier version of this had it
   * backwards. It caught the failure, reported it, and cleared the local state
   * anyway — reasoning that a failed call must not strand somebody in a list
   * they asked to leave. But the local state is not what lists them. The
   * SERVER is. Clearing it locally after a failed DELETE showed "Nearby" to a
   * person the server was still publishing in a room, which is the one lie a
   * consent control must never tell.
   *
   * So the failure propagates and nothing local changes. The screen goes on
   * saying Same Space, which is TRUE, and tapping Nearby again retries. The
   * repo already treats "asked to be hidden" this seriously — see
   * `hideOnServer` and its retry loop — and leaving a space is the same kind
   * of promise.
   */
  async leaveSameSpace() {
    await presenceApi.leaveSameSpace();
    set({
      sameSpaceVenueId: null,
      sameSpaceVenueName: null,
      sameSpaceActive: false,
      nearby: [],
      nearbyCursor: null,
    });
    await get().refreshNearby({ force: true });
  },

  async saveNearbyRadius(radiusM) {
    const nearbyRadiusM = clampNearbyRadiusM(radiusM);
    const current = clampNearbyRadiusM(
      useAuthStore.getState().user?.nearbyRadiusM,
    );
    if (nearbyRadiusM === current) return false;

    await useAuthStore.getState().setProfile({ nearbyRadiusM });
    // Forced: the radius just changed, so the cached nearby list is answering
    // the old question. Skipping here makes changing your range look broken.
    await get().refreshNearby({ force: true });
    return true;
  },

  async savePeopleFilters(filters) {
    const current = useAuthStore.getState().user?.peopleFilters ?? null;
    // Compared field by field rather than by reference: the modal rebuilds the
    // object on every tap, so a reference check would write on every render and
    // re-read the list each time.
    if (samePeopleFilters(current, filters)) return false;

    await useAuthStore.getState().setProfile({ peopleFilters: filters });
    await get().refreshNearby({ force: true });
    return true;
  },

  async refreshInbox() {
    const epoch = sessionEpoch;
    try {
      const inbox = await cuesApi.inbox();
      // A reset happened while this was on the wire: these are the previous
      // account's rows, answered to the previous account's token. Drop them.
      if (epoch !== sessionEpoch) return;
      set({
        inbox,
        nearby: withoutPendingCuePeers(get().nearby, inbox, get().sentCues),
      });
      // The server's answer to "which Cues are still waiting", so it is also
      // the answer to which Cue notifications still have something behind
      // them. Anything in the tray that this list does not vouch for is swept:
      // a Cue that expired while the handset was off, or one answered from
      // another device. Fire-and-forget — tidying the tray must never delay
      // the list that is already on screen.
      void reconcileCueNotifications(inbox.map((cue) => cue.id));
    } finally {
      // In a finally, like the other three: settled means asked-and-answered,
      // including a refusal — see `inboxSettled` on the state type. But only
      // for THIS session's ask: a stale promise from before sign-out must not
      // vouch for the next account's empty lists.
      if (epoch === sessionEpoch) set({ inboxSettled: true });
    }
  },

  async refreshSentCues() {
    const epoch = sessionEpoch;
    try {
      const sentCues = await cuesApi.sent();
      if (epoch !== sessionEpoch) return;
      set({
        sentCues,
        sentCueIds: pendingSentPeerIds(sentCues),
        nearby: withoutPendingCuePeers(get().nearby, get().inbox, sentCues),
      });
    } finally {
      if (epoch === sessionEpoch) set({ sentCuesSettled: true });
    }
  },

  async cancelSentCue(cueId) {
    const before = get().sentCues;
    const cue = before.find((item) => item.id === cueId);
    // Dropped locally first: the row is the thing the person just tapped, and
    // leaving it there until a round trip lands reads as the tap not working.
    const sentCues = before.filter((item) => item.id !== cueId);
    set({ sentCues, sentCueIds: pendingSentPeerIds(sentCues) });
    try {
      await cuesApi.cancel(cueId);
    } catch (error) {
      // Put it back. A cue that could not be withdrawn is still out there, and
      // hiding it would leave the sender believing they had a slot they do not.
      set({ sentCues: before, sentCueIds: pendingSentPeerIds(before) });
      throw error;
    }
    // Nearby hides people you have a pending cue with, so dropping the row
    // locally would put this person straight back on the map — while the
    // server's cancel cooldown still refuses a fresh cue to them. Re-ask.
    if (cue) void get().refreshNearby();
  },

  /**
   * Reload the first page of both inbox sections.
   *
   * A refresh replaces rather than appends, and deliberately drops whatever had
   * been paged in: the list is ordered by recency and reorders on every
   * message, so merging a fresh first page into stale later pages would show
   * conversations twice and leave holes where rows had moved up. Starting over
   * is the only version that is always correct, and it is what the user asked
   * for by refreshing.
   */
  async refreshChats() {
    const epoch = sessionEpoch;
    try {
      const inbox = await chatApi.list();
      if (epoch !== sessionEpoch) return;
      // Merged by id, live wins — the sections are disjoint when the server is
      // right, and one row per conversation even when it is not: a duplicate id
      // here reaches the FlatList as a key collision and renders twice.
      const seen = new Set<string>();
      const sessions: typeof inbox.live.sessions = [];
      for (const chat of [...inbox.live.sessions, ...inbox.inactive.sessions]) {
        if (seen.has(chat.id)) continue;
        seen.add(chat.id);
        sessions.push(chat);
      }
      set({
        chats: sessions,
        // Replaced wholesale, from the server's counts, for the same reason the
        // rows are: the server counted from the read marks, so its number
        // already includes everything the socket incremented since — and unlike
        // the socket counter it survives a restart. The chat on screen is being
        // read, so it never shows a badge (its mark converges via markRead).
        unreadByChat: Object.fromEntries(
          sessions
            .filter(
              (chat) =>
                (chat.unreadCount ?? 0) > 0 && chat.id !== get().activeChatId,
            )
            .map((chat) => [chat.id, chat.unreadCount as number]),
        ),
        chatSectionTotals: {
          live: inbox.live.totalCount,
          inactive: inbox.inactive.totalCount,
        },
        chatCursors: {
          live: inbox.live.nextCursor,
          inactive: inbox.inactive.nextCursor,
        },
      });
    } finally {
      if (epoch === sessionEpoch) set({ chatsSettled: true });
    }
  },

  /**
   * The Open section — my contacts who are open to chat right now.
   *
   * One request, its own cursor, and a whole-array replace rather than a merge
   * into `chats`. The rows overlap with Active by construction (Open is a
   * strict subset), and that is fine: they live in different tabs, so there is
   * no key collision, and unread counts come from `unreadByChat` by id, which
   * both tabs already share.
   *
   * A replace and not a merge, because openness EXPIRES. Merging would let a
   * contact whose lease lapsed between two loads linger in the list, which is
   * the one thing this tab must never show.
   */
  async refreshOpenChats() {
    // Collapse concurrent callers onto one request — but only callers from
    // THIS session. See `openRefresh`.

    const read = async () => {
      const epoch = sessionEpoch;
      try {
        const page = await chatApi.openSection();
        if (epoch !== sessionEpoch) return;
        set({
          openChats: page.sessions,
          openTotal: page.totalCount,
          openCursor: page.nextCursor,
          // The same rule every other paged read uses — set what this page
          // says, CLEAR what it says is zero. The version this replaces merged
          // only the positive counts, so a conversation read elsewhere kept its
          // badge here until a full inbox refresh happened to overwrite it.
          unreadByChat: withPageUnread(
            get().unreadByChat,
            page.sessions,
            get().activeChatId,
          ),
          // Cleared only here, on a response that actually arrived.
          openFailed: false,
        });
      } catch (error) {
        // Recorded and RETHROWN. The callers report it (`captureException`),
        // and the screen needs to know the difference between "read it, there
        // is nobody" and "could not read it" — those are different sentences,
        // and only one of them is a fact about other people.
        if (epoch === sessionEpoch) set({ openFailed: true });
        throw error;
      } finally {
        if (epoch === sessionEpoch) set({ openSettled: true });
      }
    };

    return openRefresh(sessionEpoch, read);
  },

  /** Append the next page of Open. Merged by id, as the other sections are. */
  async loadMoreOpenChats() {
    const cursor = get().openCursor;
    if (!cursor || get().loadingMoreOpen) return;
    const epoch = sessionEpoch;
    set({ loadingMoreOpen: true });
    try {
      const page = await chatApi.openSection({ cursor });
      if (epoch !== sessionEpoch) return;
      const byId = new Map(get().openChats.map((chat) => [chat.id, chat]));
      for (const chat of page.sessions) byId.set(chat.id, chat);
      set({
        openChats: [...byId.values()],
        openCursor: page.nextCursor,
        openTotal: page.totalCount,
        // This was missing entirely, so every badge past page 1 of Open was
        // simply absent — the rows render from `unreadByChat`, and nothing had
        // ever put them there.
        unreadByChat: withPageUnread(
          get().unreadByChat,
          page.sessions,
          get().activeChatId,
        ),
      });
    } finally {
      if (epoch === sessionEpoch) set({ loadingMoreOpen: false });
    }
  },

  /**
   * Load one conversation by id if the list does not already hold it.
   *
   * Merged in rather than replacing the list, and deliberately a no-op when
   * the row is already there: the paged list is the source for ordering and
   * unread counts, and a single-row read knows nothing about either.
   *
   * Failure is swallowed on purpose. The caller is a screen that is already
   * rendering; a 404 means the conversation is genuinely gone (blocked,
   * deleted account) and the screen's own empty state is the right answer,
   * while a network blip must not throw inside an effect.
   */
  async ensureChat(chatId: string) {
    if (!needsChatSession(get().chats, chatId)) return;
    try {
      const chat = await chatApi.get(chatId);
      // Re-checked through `mergeChatSession` rather than trusting the guard
      // above: the await means a refresh or a socket patch can have added
      // this row while the read was in flight.
      set((state) => ({ chats: mergeChatSession(state.chats, chat) }));
    } catch {
      // Left to the screen: it already handles having no conversation.
    }
  },

  /**
   * Append the next page of one section.
   *
   * Merged by id rather than concatenated: a conversation can move between
   * pages while the user scrolls — someone sends a message and it jumps to the
   * top — and a plain concat would then render it twice.
   */
  async loadMoreChats(section: "live" | "inactive") {
    const cursor = get().chatCursors[section];
    if (!cursor || get().loadingMoreChats) return;
    const epoch = sessionEpoch;
    set({ loadingMoreChats: true });
    try {
      const inbox = await chatApi.list(
        section === "live" ? { liveCursor: cursor } : { inactiveCursor: cursor },
      );
      // `loadMoreNearby` survives a cross-reset page via its cursor check;
      // this one merges by id with no such check, so without the epoch a page
      // of the previous account's conversations would merge straight into the
      // store sign-out just emptied.
      if (epoch !== sessionEpoch) return;
      const page =
        section === "live" ? inbox.live.sessions : inbox.inactive.sessions;
      const byId = new Map(get().chats.map((chat) => [chat.id, chat]));
      for (const chat of page) byId.set(chat.id, chat);
      set({
        chats: [...byId.values()],
        // Counts only for the rows this page carried — the rest of the map is
        // someone else's truth and a page must not rewrite it.
        unreadByChat: withPageUnread(
          get().unreadByChat,
          page,
          get().activeChatId,
        ),
        chatSectionTotals: {
          live: inbox.live.totalCount,
          inactive: inbox.inactive.totalCount,
        },
        chatCursors: {
          ...get().chatCursors,
          [section]:
            section === "live" ? inbox.live.nextCursor : inbox.inactive.nextCursor,
        },
      });
    } finally {
      // Epoch-guarded like the writes above: after a reset this flag belongs
      // to the next session, whose own loadMore may already be in flight.
      if (epoch === sessionEpoch) set({ loadingMoreChats: false });
    }
  },

  async refreshGroupInbox() {
    const epoch = sessionEpoch;
    try {
      const sections = await groupApi.inbox();
      if (epoch !== sessionEpoch) return;
      const activeGroupId = get().activeGroupId;
      const rows = [...sections.active, ...sections.inactive.groups];
      set({
        // The group open on screen is being read, so never show it as unread.
        groupInbox: activeGroupId
          ? rows.map((group) =>
              group.id === activeGroupId ? { ...group, unreadCount: 0 } : group,
            )
          : rows,
        // A refresh is the FIRST page again: the cursor restarts rather than
        // surviving from a previous, possibly reordered, walk.
        groupInactiveCursor: sections.inactive.nextCursor,
        groupInactiveTotal: sections.inactive.totalCount,
      });
    } finally {
      if (epoch === sessionEpoch) set({ groupInboxSettled: true });
    }
  },

  async loadMoreInactiveGroups() {
    const cursor = get().groupInactiveCursor;
    if (!cursor || get().loadingMoreGroups) return;
    const epoch = sessionEpoch;
    set({ loadingMoreGroups: true });
    try {
      const sections = await groupApi.inbox({ inactiveCursor: cursor });
      // Same epoch rule as `loadMoreChats`: this merges by id with no cursor
      // check, so a page from the previous account must never merge into the
      // store a sign-out just emptied.
      if (epoch !== sessionEpoch) return;
      const byId = new Map(get().groupInbox.map((group) => [group.id, group]));
      for (const group of sections.inactive.groups) byId.set(group.id, group);
      set({
        groupInbox: [...byId.values()],
        groupInactiveCursor: sections.inactive.nextCursor,
        groupInactiveTotal: sections.inactive.totalCount,
      });
    } finally {
      if (epoch === sessionEpoch) set({ loadingMoreGroups: false });
    }
  },

  /**
   * The bulk refresh, optionally SCHEDULED rather than fired all at once.
   *
   * Called with no options it behaves exactly as before, because the launch
   * path must not change: Cues, Inbox, Chats and Groups are preloaded so those
   * tabs are instant, and that is deliberate product work this must not undo.
   *
   * The reconnect path passes jitter and gaps. See `refresh-schedule.ts` for
   * why jitter is the part that matters — staggering smooths one client,
   * jitter scatters a fleet, and a fleet-wide reconnect is the synchronised
   * case.
   *
   * There are exactly TWO phases, and the boundary is where it is for one
   * reason: Nearby is the only read that may lag.
   *
   *   1. The check-in AND the four list reads, CONCURRENTLY. The concurrency
   *      is load-bearing and must not be tidied into a sequence. Read
   *      `refreshVenue`: it awaits `getCurrentCoords`, a GPS acquisition
   *      budgeted at up to 12 s, and then possibly `publishPresence`, which
   *      its own comment notes "can take seconds". Putting the four ~1 ms list
   *      reads behind a phase boundary from that parks Cues, Inbox, Chats and
   *      Groups behind a satellite fix — which is precisely the wait the
   *      preload exists to remove. An earlier version of this scheduler did
   *      exactly that; `tab-first-paint.test.ts` now fails if it recurs.
   *   2. Nearby, alone, after the gap. Measured at ~6 ms warm and ~31 ms cold
   *      — more than half the cost of an app open on its own, and the one read
   *      the app stays usable without.
   *
   * `refreshVenue` is listed first inside phase one, but that is cosmetic and
   * must not be read as an ordering guarantee — it is very nearly the opposite
   * of one. The four list reads each `await` their fetch as their first
   * statement, so all four requests leave in the first synchronous tick.
   * `refreshVenue` has to clear `hydrateSpaceState` (a storage read) and then
   * `getCurrentCoords` before it calls `venuesApi.current`, so the check-in is
   * in practice the LAST of the five to reach the wire, not the first.
   *
   * That is fine — nothing waits on it, which is the property that matters —
   * and it is worth knowing for a second reason: it means the check-in is
   * already dispersed across a fleet by the variance in GPS acquisition, while
   * the four list reads are not dispersed at all. The synchronised part of a
   * fleet-wide reconnect is those four. See `refresh-schedule.ts`.
   */
  async refreshAll(options) {
    const gapMs = options?.gapMs ?? 0;
    await runRefreshPhases(
      [
        {
          gapMs: 0,
          run: () =>
            Promise.all([
              get().refreshVenue(),
              get().refreshInbox(),
              get().refreshSentCues(),
              get().refreshChats(),
              get().refreshGroupInbox(),
            ]),
        },
        { gapMs, run: () => get().refreshNearby() },
      ],
      {
        jitterMs: options?.jitterMs ?? 0,
        // The phases no longer reject, so the tab layout's `catch` around the
        // launch preload can never fire. Without this line a failed preload
        // would be invisible — 401 is still handled globally by
        // `setUnauthorizedHandler` in `auth-store.ts`, but nothing else was.
        onError: (error) => captureException(error, { scope: "session.refreshAll" }),
      },
    );
  },

  async searchVenues(q, coords) {
    const result = coords ? null : await getCurrentCoords();
    const searchCoords =
      coords ?? (result?.status === "ok" ? result.coords : undefined);
    return venuesApi.search(q, searchCoords);
  },

  async nearbyVenues(coords) {
    if (coords) return venuesApi.nearby(coords);
    const result = await getCurrentCoords();
    if (result.status !== "ok") return [];
    return venuesApi.nearby(result.coords);
  },

  async joinVenue(candidate, coords) {
    const result = coords ? null : await getCurrentCoords();
    const gps = coords ?? (result?.status === "ok" ? result.coords : undefined);
    if (!gps) {
      throw new Error("GPS is required to refine your current space");
    }
    const venue = await venuesApi.join(candidate, gps);
    set({ venue });
    if (
      shouldPublish({
        desired: get().visibilityStatus,
        cuesManuallyPaused: get().cuesManuallyPaused,
        blocked: get().publishBlocked,
      })
    ) {
      try {
        await publishPresence();
      } catch (error) {
        // The venue is already written and on screen. What failed is publishing
        // AT it, and that is the home screen's condition to own — not the
        // editor's, which used to raise a toast for it. Recording it here is
        // what puts a stale fix into the automatic retry and an imprecise one in
        // front of the user with the two things that actually help.
        const refusal = visibilityRefusalReason(error);
        if (refusal) recordNearbyFailure(nearbyErrorForRefusal(refusal));
        throw error;
      }
    } else {
      await presenceApi.setVisibility(false);
      set({ ...PUBLISH_SETTLED });
    }
    await get().refreshNearby({ force: true });
  },

  async joinSpace() {
    // Refresh WHERE before asserting THAT — the same order, and for the same
    // reason, as the heartbeat. `POST /presence/join` writes `last_seen`,
    // `visible` and `status` and touches no coordinates, so joining on a stale
    // row published an old position stamped fresh. The server now refuses that
    // outright; this is what stops the refusal reaching a user who has done
    // nothing wrong.
    //
    // `refreshVenue` and `setVisibility` already checked in first. This was the
    // one path that did not.
    //
    // A claimed intent like its siblings (review, 2026-08-26): this path used
    // to set `visibilityStatus: "visible"` unconditionally AFTER its awaited
    // publish, so a hide tapped mid-flight was resurrected locally the moment
    // the publish resolved — undoing the corrector's server-side hide from the
    // client side. No production caller reaches this today; the guard is for
    // whichever screen eventually does.
    const seq = claimVisibilityIntent({
      visibilityStatus: "visible",
      cuesManuallyPaused: false,
    });
    explicitRenewalOwed = true;
    await freshenPositionBeforeJoining();
    if (seq !== visibilityIntentSeq) return;

    // Opt in to being seen/cued in the current space, then refresh the list.
    // Explicit tap, so the lease-renewing endpoint.
    await publishPresenceExplicit();
    if (seq !== visibilityIntentSeq) return;
    set({ ...PUBLISH_SETTLED });
    await persistSpaceState({
      joined: true,
      visibilityStatus: "visible",
      cuesManuallyPaused: false,
    });
    if (seq !== visibilityIntentSeq) return;
    onBecameVisible(get().venue?.id ?? null);
    // Forced here and in every sibling below: the presence write above changed
    // the thing being read, so a skipped read would show the state we just left.
    await get().refreshNearby({ force: true });
  },

  async setVisibility(visible) {
    const visibilityStatus = visible ? "visible" : "invisible";
    const cuesManuallyPaused = visible ? false : get().cuesManuallyPaused;

    // One tap, one move. Everything after this line is the server catching up.
    const seq = claimVisibilityIntent({ visibilityStatus, cuesManuallyPaused });
    void persistSpaceState({ joined: true, visibilityStatus, cuesManuallyPaused });
    // Owed BEFORE `onBecameVisible`: that call starts the heartbeat, the
    // heartbeat beats immediately, and an immediate beat that raced ahead of
    // the explicit publish used to go through non-renewing `join()`.
    if (visible) explicitRenewalOwed = true;
    if (visible) onBecameVisible(get().venue?.id ?? null);
    else onBecameInvisible();

    if (!visible) {
      // Never rethrown: the user asked to be hidden and the switch says hidden,
      // so an alert here would report a problem they cannot act on about a
      // state they already have. `hideOnServer` keeps trying.
      await hideOnServer(seq);
      if (seq !== visibilityIntentSeq) return;
      await get().refreshVenue({ force: true });
      return;
    }

    // Going visible still AWAITS and still throws. A refusal names which part
    // of the position was wrong, and that sentence is the only actionable thing
    // the caller can show — see `onToggleVisibility`.
    //
    // Segment probes, because build 12 measured the WHOLE of this at 8.95s
    // without saying which part. Each awaited leg is timed separately so the
    // next device pass attributes rather than guesses.
    const endFreshen = probeSpan("visible.freshen");
    await freshenPositionBeforeJoining();
    endFreshen();
    if (seq !== visibilityIntentSeq) return;
    const endPublish = probeSpan("visible.publish");
    // Explicit tap, so the lease-renewing endpoint.
    await publishPresenceExplicit();
    endPublish();
    if (seq !== visibilityIntentSeq) return;
    // Only the venue is awaited: it decides what the screen SAYS about where
    // this person is, and a caller that resolves before that would hand back a
    // stale label. The nearby list is a different screen's data — it repaints
    // when it arrives and nothing waits on it.
    const endVenue = probeSpan("visible.venue");
    await get().refreshVenue({ force: true });
    endVenue();
    void get().refreshNearby({ force: true });
  },

  async setCueAvailabilityPaused(paused) {
    // The same switch wearing different words — it must feel the same.
    const visibilityStatus = paused ? "invisible" : "visible";
    const seq = claimVisibilityIntent({
      visibilityStatus,
      cuesManuallyPaused: paused,
    });
    void persistSpaceState({
      joined: true,
      visibilityStatus,
      cuesManuallyPaused: paused,
    });
    // Same ordering rule as `setVisibility`: owed before the heartbeat starts.
    if (!paused) explicitRenewalOwed = true;
    if (paused) onBecameInvisible();
    else onBecameVisible(get().venue?.id ?? null);

    if (paused) {
      await hideOnServer(seq);
      if (seq !== visibilityIntentSeq) return;
      await get().refreshVenue({ force: true });
      return;
    }

    // Unpausing is becoming visible again, so it needs a current position for
    // the same reason joining does.
    await freshenPositionBeforeJoining();
    if (seq !== visibilityIntentSeq) return;
    // The same switch as go-visible wearing different words — and the same
    // explicit tap, so the lease-renewing endpoint.
    await publishPresenceExplicit();
    if (seq !== visibilityIntentSeq) return;
    await get().refreshVenue({ force: true });
    await get().refreshNearby({ force: true });
  },


  receiveCue(cue) {
    set({
      inbox: get().inbox.some((item) => item.id === cue.id)
        ? get().inbox
        : [cue, ...get().inbox],
      nearby: get().nearby.filter((user) => user.id !== cue.fromUserId),
    });
  },

  removeNearbyUser(userId) {
    set({
      nearby: get().nearby.filter((user) => user.id !== userId),
    });
  },

  setNearbyListVisible(visible) {
    // Guarded because the caller is a render-driven effect. Zustand notifies
    // every subscriber on any `set`, so writing an unchanged value here would
    // re-render the list on each of its own re-renders.
    if (get().nearbyListVisible === visible) return;
    set({ nearbyListVisible: visible });
  },

  markNearbyDirty() {
    if (get().nearbyDirty) return;
    set({ nearbyDirty: true });
  },

  receiveChatMessage(chatId, messageId, patch) {
    // The same realtime message can arrive more than once — count it only once
    // so the unread badge doesn't multiply.
    if (messageId && !markMessageSeen(messageId)) return;

    // The payload carries the row's own preview and timestamp, so the inbox row
    // is updated from it rather than re-read. When it cannot — a cue acceptance
    // naming a conversation the client has never seen, or a reveal, neither of
    // which the payload describes — read at once instead; those are rare and
    // event-driven, unlike a message, which is not.
    const { rows, patched } = applyChatMessage(get().chats, {
      chatId,
      preview: patch?.preview,
      sentAt: patch?.sentAt,
    });
    if (patched) {
      set({ chats: rows });
      // `inactiveAt`, `inactiveWarning` and `state` can move on a received
      // message, but only when this user had already sent since the last mutual
      // mark — which is not knowable here. Converge them on the window.
      chatsReconcile.request();
    } else {
      chatsReconcile.requestNow();
    }

    // Ignore messages for the conversation the user is currently reading.
    if (get().activeChatId === chatId) return;
    const current = get().unreadByChat[chatId] ?? 0;
    set({
      unreadByChat: { ...get().unreadByChat, [chatId]: current + 1 },
    });
  },

  receiveChatReveal(chatId, update) {
    const { chats, patched } = applyChatReveal(get().chats, chatId, update);
    if (patched) set({ chats });
    return patched;
  },

  setActiveChat(chatId) {
    set({ activeChatId: chatId });
    if (chatId) get().markChatRead(chatId);
  },

  markChatRead(chatId) {
    if (get().unreadByChat[chatId]) {
      const next = { ...get().unreadByChat };
      delete next[chatId];
      set({ unreadByChat: next });
    }
    // Always posted, even with no local count to clear — the local map only
    // knows about messages this process saw arrive, and the whole point of
    // the server-side mark is the ones it did not. Same shape as
    // `markGroupRead`: fire-and-forget, the next list read confirms it.
    void chatApi.markRead(chatId).catch(() => {});
  },

  receiveGroupMessage(groupId, patch) {
    // A message arrived in a group the user belongs to. The payload carries the
    // preview and timestamp `inbox()` would have returned, and the sender is
    // filtered out before the event is emitted, so the +1 is the same number the
    // server counts — the row is patched rather than the whole inbox re-read.
    const { rows, patched } = applyGroupMessage(get().groupInbox, {
      groupId,
      preview: patch?.preview,
      sentAt: patch?.sentAt,
      activeGroupId: get().activeGroupId,
    });
    if (patched) {
      set({ groupInbox: rows });
      groupInboxReconcile.request();
    } else {
      // A group the inbox does not hold yet — it has to be read to appear.
      groupInboxReconcile.requestNow();
    }
  },

  setActiveGroup(groupId) {
    set({ activeGroupId: groupId });
    if (groupId) get().markGroupRead(groupId);
  },

  markGroupRead(groupId) {
    const target = get().groupInbox.find((group) => group.id === groupId);
    if (target?.unreadCount) {
      set({
        groupInbox: get().groupInbox.map((group) =>
          group.id === groupId ? { ...group, unreadCount: 0 } : group,
        ),
      });
    }
    void groupApi.markRead(groupId).catch(() => {});
  },

  /**
   * Blocking removes the conversation from the list. Exiting quietly does not:
   * the thread survives in the Inactive tab, so dropping it here would make it
   * look deleted until the next refresh — the one thing the new lifecycle
   * promises never happens.
   */
  async endChat(chatId, outcome = "quiet") {
    await chatApi.end(chatId, outcome);
    const nextUnread = { ...get().unreadByChat };
    delete nextUnread[chatId];
    set({
      chats:
        outcome === "blocked"
          ? get().chats.filter((chat) => chat.id !== chatId)
          : get().chats,
      unreadByChat: nextUnread,
      activeChatId: get().activeChatId === chatId ? null : get().activeChatId,
    });
    if (outcome !== "blocked") await get().refreshChats();
    // NO nearby refresh, and the comment that used to justify one — "ending a
    // conversation can put the other person back on the nearby list" — was
    // describing an architecture that no longer exists.
    //
    // Nearby excludes a pair on `user_pairs.connected_at IS NOT NULL`
    // (`noPairExclusionSql`), and that column is first-write-wins and never
    // moves: `upsertPair`'s patch type structurally cannot reach it, so neither
    // ending a chat nor blocking nor unblocking can clear it. A quiet exit
    // writes `status: "clear"` and nothing else — and `user-pair.entity.ts`
    // records that treating `clear` as "never connected" is precisely the
    // correctness bug this column was added to prevent.
    //
    // So a peer whose chat just ended was ALREADY absent from the list and
    // stays absent, in both outcomes: they have met, and Nearby is strangers.
    // The refresh could only ever return the same list, at the cost of a full
    // first-page read. Re-adding one to "clean up a stale row" would be
    // defending an invariant with a fallback instead of trusting it.
  },

  /** Wipe my copy of a thread. Leaves the conversation itself alone. */
  async clearChat(chatId) {
    const updated = await chatApi.clear(chatId);
    const nextUnread = { ...get().unreadByChat };
    delete nextUnread[chatId];
    set({
      chats: get().chats.map((chat) =>
        chat.id === chatId ? { ...chat, ...updated, lastMessage: undefined } : chat,
      ),
      unreadByChat: nextUnread,
    });
    return updated;
  },

  async unblock(chatId) {
    const updated = await chatApi.unblock(chatId);
    set({
      chats: get().chats.map((chat) =>
        chat.id === chatId ? { ...chat, ...updated } : chat,
      ),
    });
    return updated;
  },

  async requestReopen(chatId) {
    const updated = await chatApi.requestReopen(chatId);
    set({
      chats: get().chats.map((chat) =>
        chat.id === chatId ? { ...chat, ...updated } : chat,
      ),
    });
    return updated;
  },

  async resume(chatId) {
    const updated = await chatApi.resume(chatId);
    set({
      chats: get().chats.map((chat) =>
        chat.id === chatId ? { ...chat, ...updated } : chat,
      ),
    });
    return updated;
  },

  async respondToReopen(chatId, accept) {
    const updated = await chatApi.respondToReopen(chatId, accept);
    set({
      chats: get().chats.map((chat) =>
        chat.id === chatId ? { ...chat, ...updated } : chat,
      ),
    });
    return updated;
  },

  async sendCue(toUserId) {
    let cue: Cue | null = null;
    try {
      cue = await cuesApi.send(toUserId);
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 400 &&
        err.message.includes("checked into a venue")
      ) {
        // Forced: this is the recovery from "you are not checked in". Riding a
        // cached read would re-send the cue into the same failure.
        await get().refreshVenue({ force: true });
        cue = await cuesApi.send(toUserId);
      } else {
        throw err;
      }
    }

    set({
      sentCueIds: unique(toUserId, get().sentCueIds),
      sentCues:
        cue && !get().sentCues.some((item) => item.id === cue.id)
          ? [cue, ...get().sentCues]
          : get().sentCues,
    });
    // Hold the row for a beat so the `Sent` button is actually seen, then drop
    // it. The timer starts here rather than after the refresh below: the point
    // is a second of screen time from the moment the button changes, not a
    // second measured from whenever the network happened to come back.
    holdSentRow(toUserId);
    setTimeout(() => {
      releaseSentRow(toUserId);
      set({
        nearby: withoutPendingCuePeers(get().nearby, get().inbox, get().sentCues),
      });
    }, SENT_ROW_LINGER_MS);

    // Reflect the new outgoing cue in the Cues tab. This would also drop the
    // peer from Nearby, which is what the hold above defers; it is not awaited
    // by the linger, so a slow refresh cannot extend the row's stay.
    await get().refreshSentCues();
  },

  async respondToCue(cueId, status) {
    const chat = await cuesApi.respond(cueId, status);
    set({ inbox: get().inbox.filter((c) => c.id !== cueId) });
    if (chat) await get().refreshChats();
    return chat;
  },
}));

registerSessionReset(() => useSessionStore.getState().resetSpaceState());

// The rendered-empty auto-fill trigger — see `autoFillNearbyIfRenderedEmpty`
// for why this watches the state rather than being called from the sites
// that empty the list. Conditions in order: a list with people (or with no
// next page) clears the give-up marker and needs no fill; the marker skips
// the continuation a previous fill already gave up on; a refresh in flight
// is about to replace everything, so filling behind it would burn the cap
// on pages `loadMoreNearby` refuses to fetch; and the in-flight flag keeps
// the fill's own writes from relaunching it.
useSessionStore.subscribe((state) => {
  if (state.nearby.length > 0 || state.nearbyCursor === null) {
    nearbyGiveUpCursor = null;
    return;
  }
  if (
    state.nearbyCursor === nearbyGiveUpCursor ||
    state.loadingNearby ||
    nearbyAutoFillInFlight
  ) {
    return;
  }
  void autoFillNearbyIfRenderedEmpty(() => useSessionStore.getState());
});

// When a location break is detected, mark the user hidden (only if they were
// visible) so we never keep pretending they're present.
registerStayVisibleHideHandler(() => {
  const session = useSessionStore.getState();
  if (session.visibilityStatus === "visible") {
    void session.setVisibility(false);
  }
});

// And the read in the other direction, for the same cycle-avoiding reason.
// `reconcile` may start the background task, whose next wake calls
// POST /presence/join — so it must never run for somebody who is hidden, or
// the app would put them back on other people's lists without being asked.
registerStayVisibleVisibilityReader(
  () => useSessionStore.getState().visibilityStatus === "visible",
);
